// ============================================================================
// EDUSPHERE ENTERPRISE - API SERVICE LAYER
// HTTP API client for all backend endpoints.
// Uses axios-based apiClient with JWT auth, token refresh, and error handling.
// ============================================================================

import apiClient from './apiClient';

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Standardised error handler - catches axios errors and returns
 * the same { success, error } shape the frontend already expects.
 */
const handleError = (error: any) => {
  const message =
    error.response?.data?.error?.message ||
    error.response?.data?.error ||
    error.response?.data?.message ||
    error.message ||
    'An unexpected error occurred';
  return { success: false, error: message };
};

/**
 * Transform paginated backend response to the shape frontend expects.
 * Backend: { success, data: { data: T[], meta: { total, page, ... } } }
 * Frontend: { success, data: T[], pagination: { total, page, ... } }
 */
const transformPaginated = (response: any) => {
  const raw = response.data;
  if (raw?.success && raw?.data?.data && raw?.data?.meta) {
    return {
      success: true,
      data: raw.data.data,
      pagination: raw.data.meta,
    };
  }
  // Fallback if shape is different
  return raw;
};

// ============================================================================
// AUTHENTICATION API
// Backend routes: /auth/*
// ============================================================================
export const authApi = {
  login: async (email: string, password: string) => {
    try {
      const response = await apiClient.post('/auth/login', { email, password });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  logout: async () => {
    try {
      const response = await apiClient.post('/auth/logout');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getCurrentUser: async () => {
    try {
      const response = await apiClient.get('/auth/me');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  forgotPassword: async (email: string) => {
    try {
      const response = await apiClient.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  resetPassword: async (token: string, newPassword: string) => {
    try {
      const response = await apiClient.post('/auth/reset-password', { token, newPassword });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  verifyEmail: async (token: string) => {
    try {
      const response = await apiClient.post('/auth/verify-email', { token });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  sendVerification: async () => {
    try {
      const response = await apiClient.post('/auth/send-verification');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getUsers: async (params?: any) => {
    try {
      const response = await apiClient.get('/auth/users', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  register: async (data: any) => {
    try {
      const response = await apiClient.post('/auth/register', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateUser: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/auth/users/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteUser: async (id: string) => {
    try {
      const response = await apiClient.delete(`/auth/users/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  changePassword: async (data: { oldPassword: string; newPassword: string }) => {
    try {
      // Backend changePasswordDto (auth.routes.ts:16) expects { oldPassword, newPassword } via PUT.
      const response = await apiClient.put('/auth/change-password', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  logoutAll: async () => {
    try {
      const response = await apiClient.post('/auth/logout-all');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateProfile: async (data: any) => {
    try {
      const response = await apiClient.put('/auth/profile', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ----------------------------------------------------------------------
  // Two-Factor Authentication (TOTP)
  // ----------------------------------------------------------------------
  setup2fa: async () => {
    try {
      const response = await apiClient.post('/auth/2fa/setup');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  enable2fa: async (code: string) => {
    try {
      const response = await apiClient.post('/auth/2fa/enable', { code });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  disable2fa: async (code: string) => {
    try {
      const response = await apiClient.post('/auth/2fa/disable', { code });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Public endpoint — completes login after a mfaRequired response, using
  // the short-lived mfaToken returned by /auth/login instead of a session.
  verify2fa: async (mfaToken: string, code: string) => {
    try {
      const response = await apiClient.post('/auth/2fa/verify', { mfaToken, code });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// STUDENTS API
// Backend routes: /students/*
// ============================================================================
export const studentsApi = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    classId?: string;
    sectionId?: string;
    status?: string;
    gender?: string;
    academicYearId?: string;
    branchId?: string;
  }) => {
    try {
      const response = await apiClient.get('/students', { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },

  getById: async (id: string) => {
    try {
      const response = await apiClient.get(`/students/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Resolve a student by admission number (canonical post-admission identifier).
  getByAdmissionNumber: async (admissionNumber: string) => {
    try {
      const response = await apiClient.get(`/students/by-admission/${encodeURIComponent(admissionNumber)}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  create: async (data: any) => {
    try {
      const response = await apiClient.post('/students', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  update: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/students/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  delete: async (id: string) => {
    try {
      const response = await apiClient.delete(`/students/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getAttendance: async (studentId: string, params?: { month?: number; year?: number }) => {
    try {
      const response = await apiClient.get(`/attendance/reports/student/${studentId}`, { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getMarks: async (studentId: string, examId?: string) => {
    try {
      const response = await apiClient.get(`/students/${studentId}/marks`, {
        params: examId ? { examId } : undefined,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getFees: async (studentId: string) => {
    try {
      const response = await apiClient.get(`/fees/student-ledger/${studentId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getSummary: async (params?: { branchId?: string | null; academicYearId?: string | null }) => {
    try {
      const response = await apiClient.get('/students/summary', { params: params ?? undefined });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getStrengthReport: async (params?: { academicYearId?: string }) => {
    try {
      const response = await apiClient.get('/students/reports/strength', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getDemographicsReport: async (params?: { academicYearId?: string }) => {
    try {
      const response = await apiClient.get('/students/reports/demographics', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  fullAdmission: async (data: any) => {
    try {
      const response = await apiClient.post('/students/full-admission', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateStatus: async (id: string, data: { status: string; reason: string; effectiveDate?: string }) => {
    try {
      const response = await apiClient.put(`/students/${id}/status`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  changeSection: async (id: string, data: { sectionId?: string; newSectionId?: string; reason?: string }) => {
    try {
      const payload = {
        newSectionId: data.newSectionId || data.sectionId,
        reason: data.reason,
      };
      const response = await apiClient.put(`/students/${id}/change-section`, payload);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  bulkEdit: async (data: { studentIds: string[]; updates: any }) => {
    try {
      const response = await apiClient.post('/students/bulk-edit', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  bulkImport: async (formData: FormData) => {
    try {
      const response = await apiClient.post('/students/bulk-import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  promote: async (data: { students: { studentId: string; toClassId: string; toSectionId: string; status: string }[]; toAcademicYearId: string }) => {
    try {
      const response = await apiClient.post('/students/promote', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Performance Analytics
  getPerformanceTrend: async (studentId: string) => {
    try {
      const response = await apiClient.get(`/exams/student/${studentId}/trend`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getSubjectPerformanceTrend: async (studentId: string, subjectId: string) => {
    try {
      const response = await apiClient.get(`/exams/student/${studentId}/subject/${subjectId}/trend`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getClassPerformanceComparison: async (classId: string) => {
    try {
      const response = await apiClient.get(`/exams/class/${classId}/performance`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Atomic Enrollment Orchestrator ──
  enrollDryRun: async (body: any) => {
    try {
      const response = await apiClient.post('/students/enroll/dry-run', body);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  enroll: async (body: any) => {
    try {
      const response = await apiClient.post('/students/enroll', body);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  undoEnroll: async (correlationId: string, reason: string) => {
    try {
      const response = await apiClient.post(`/students/enroll/undo/${correlationId}`, { reason });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ─── Guardian CRUD ──────────────────────────────────────────────────────────
  getGuardians: async (studentId: string) => {
    try {
      const response = await apiClient.get(`/students/${studentId}/guardians`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  addGuardian: async (studentId: string, data: any) => {
    try {
      const response = await apiClient.post(`/students/${studentId}/guardians`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateGuardian: async (studentId: string, guardianId: string, data: any) => {
    try {
      const response = await apiClient.put(`/students/${studentId}/guardians/${guardianId}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  removeGuardian: async (studentId: string, guardianId: string) => {
    try {
      const response = await apiClient.delete(`/students/${studentId}/guardians/${guardianId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  setPrimaryGuardian: async (studentId: string, guardianId: string) => {
    try {
      const response = await apiClient.put(`/students/${studentId}/guardians/${guardianId}/primary`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ─── Medical Info ────────────────────────────────────────────────────────────
  getMedicalInfo: async (studentId: string) => {
    try {
      const response = await apiClient.get(`/students/${studentId}/medical`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  upsertMedicalInfo: async (studentId: string, data: any) => {
    try {
      const response = await apiClient.put(`/students/${studentId}/medical`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ─── Sibling Linking ─────────────────────────────────────────────────────────
  getSiblings: async (studentId: string) => {
    try {
      const response = await apiClient.get(`/students/${studentId}/siblings`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  linkSibling: async (studentId: string, siblingId: string) => {
    try {
      const response = await apiClient.post(`/students/${studentId}/siblings/${siblingId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  unlinkSibling: async (studentId: string, siblingId: string) => {
    try {
      const response = await apiClient.delete(`/students/${studentId}/siblings/${siblingId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ─── Batch Enrollment ────────────────────────────────────────────────────────
  batchEnroll: async (body: {
    sharedContext: {
      classId: string;
      sectionId: string;
      admissionType?: string;
      feeStructureId?: string;
      payNow?: boolean;
      admissionPaymentDueDate?: string;
    };
    students: Array<{
      firstName: string;
      lastName: string;
      dateOfBirth: string;
      gender: string;
      category?: string;
      guardianName: string;
      guardianPhone: string;
      guardianRelation?: string;
      aadhaarNumber?: string;
      phone?: string;
      email?: string;
      religion?: string;
      motherTongue?: string;
    }>;
  }) => {
    try {
      const response = await apiClient.post('/students/enroll/batch', body);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ─── Payment Pending ────────────────────────────────────────────────────────
  getPaymentPending: async (params?: { branchId?: string; academicYearId?: string }) => {
    try {
      const response = await apiClient.get('/students/payment-pending', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ─── Stream E1 — Guardian custody hierarchy ────────────────────────────────
  getGuardianHierarchy: async (studentId: string) => {
    try {
      const response = await apiClient.get(`/students/${studentId}/guardian-hierarchy`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  setGuardianHierarchy: async (studentId: string, assignments: any[]) => {
    try {
      const response = await apiClient.put(`/students/${studentId}/guardian-hierarchy`, { assignments });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ─── Stream E1 — Caste sub-categories ──────────────────────────────────────
  casteSubcategories: {
    list: async (parent: string, params?: { state?: string }) => {
      try {
        const response = await apiClient.get('/students/caste-subcategories', {
          params: { parent, ...(params || {}) },
        });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    addCustom: async (data: {
      parentCategory: string;
      name: string;
      code?: string;
      state?: string;
      governmentOrderRef?: string | null;
    }) => {
      try {
        const response = await apiClient.post('/students/caste-subcategories', data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ─── Stream E1 — House system ──────────────────────────────────────────────
  houses: {
    list: async () => {
      try {
        const response = await apiClient.get('/students/houses');
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    create: async (data: {
      name: string;
      color?: string | null;
      captainStudentId?: string | null;
      viceCaptainStudentId?: string | null;
    }) => {
      try {
        const response = await apiClient.post('/students/houses', data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    update: async (houseId: string, data: Record<string, any>) => {
      try {
        const response = await apiClient.put(`/students/houses/${houseId}`, data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    assign: async (houseId: string, studentIds: string[]) => {
      try {
        const response = await apiClient.post(`/students/houses/${houseId}/assign`, { studentIds });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    awardPoints: async (
      houseId: string,
      data: { eventName: string; points: number; notes?: string },
    ) => {
      try {
        const response = await apiClient.post(`/students/houses/${houseId}/points`, data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    leaderboard: async () => {
      try {
        const response = await apiClient.get('/students/houses/leaderboard/current');
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ─── Stream E1 — Annual photo refresh ──────────────────────────────────────
  photoRefresh: {
    markDue: async (academicYearId: string) => {
      try {
        const response = await apiClient.post('/students/photo-refresh/mark-due', { academicYearId });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    list: async (params: {
      academicYearId: string;
      status?: 'pending' | 'captured' | 'approved' | 'rejected';
      classId?: string;
      limit?: number;
      offset?: number;
    }) => {
      try {
        const response = await apiClient.get('/students/photo-refresh', { params });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    capture: async (
      studentId: string,
      data: { academicYearId: string; photoUrl: string },
    ) => {
      try {
        const response = await apiClient.post(
          `/students/${studentId}/photo-refresh/capture`,
          data,
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    approve: async (logId: string, notes?: string) => {
      try {
        const response = await apiClient.post(`/students/photo-refresh/${logId}/approve`, { notes });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    reject: async (logId: string, reason: string) => {
      try {
        const response = await apiClient.post(`/students/photo-refresh/${logId}/reject`, { reason });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ─── Stream E1 — Re-admission after TC ─────────────────────────────────────
  readmission: {
    searchCandidates: async (params: {
      fullName?: string;
      dob?: string;
      guardianPhone?: string;
      admissionNumber?: string;
      limit?: number;
    }) => {
      try {
        const response = await apiClient.get('/students/readmission/candidates', { params });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    reactivate: async (
      studentId: string,
      data: {
        newClassId: string;
        newSectionId?: string | null;
        academicYearId: string;
        admissionDate?: string;
        reason: string;
      },
    ) => {
      try {
        const response = await apiClient.post(`/students/${studentId}/readmit`, data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    history: async (studentId: string) => {
      try {
        const response = await apiClient.get(`/students/${studentId}/readmission-history`);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ─── Stream E1 — Sibling discount ──────────────────────────────────────────
  siblingDiscount: {
    evaluate: async (studentId: string) => {
      try {
        const response = await apiClient.post(`/students/${studentId}/sibling-discount/evaluate`);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ─── Stream E1 — Conduct certificate ───────────────────────────────────────
  conduct: {
    generate: async (
      studentId: string,
      params: {
        conductGrade: 'excellent' | 'good' | 'satisfactory' | 'unsatisfactory';
        language?: 'en' | 'ta' | 'bilingual';
        additionalNotes?: string;
      },
    ) => {
      try {
        const response = await apiClient.get(`/students/${studentId}/conduct-certificate`, {
          params,
          responseType: 'blob',
        });
        return { success: true, data: response.data as Blob };
      } catch (error: any) {
        return handleError(error);
      }
    },
  },
};

// ============================================================================
// EMPLOYEES API
// Backend routes: /employees/*
// ============================================================================
export const employeesApi = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    department?: string;
    status?: string;
  }) => {
    try {
      const response = await apiClient.get('/employees', { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },

  getById: async (id: string) => {
    try {
      const response = await apiClient.get(`/employees/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  create: async (data: any) => {
    try {
      const response = await apiClient.post('/employees', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  update: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/employees/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  delete: async (id: string) => {
    try {
      const response = await apiClient.delete(`/employees/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getDepartments: async () => {
    try {
      const response = await apiClient.get('/employees/departments');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createDepartment: async (data: any) => {
    try {
      const response = await apiClient.post('/employees/departments', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateDepartment: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/employees/departments/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteDepartment: async (id: string) => {
    try {
      const response = await apiClient.delete(`/employees/departments/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getDesignations: async () => {
    try {
      const response = await apiClient.get('/employees/designations');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createDesignation: async (data: any) => {
    try {
      const response = await apiClient.post('/employees/designations', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateDesignation: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/employees/designations/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteDesignation: async (id: string) => {
    try {
      const response = await apiClient.delete(`/employees/designations/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Employee Documents ──
  getDocuments: async (employeeId: string) => {
    try {
      const response = await apiClient.get(`/employees/${employeeId}/documents`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  uploadDocument: async (employeeId: string, data: any) => {
    try {
      const response = await apiClient.post(`/employees/${employeeId}/documents`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteDocument: async (employeeId: string, documentId: string) => {
    try {
      const response = await apiClient.delete(`/employees/${employeeId}/documents/${documentId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  verifyDocument: async (employeeId: string, documentId: string) => {
    try {
      const response = await apiClient.put(`/employees/${employeeId}/documents/${documentId}/verify`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getDocumentTypes: async () => {
    try {
      const response = await apiClient.get("/employees/document-types");
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Career History (promotions / transfers) ──
  getHistory: async (id: string) => {
    try {
      const response = await apiClient.get(`/employees/${id}/history`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  promote: async (
    id: string,
    payload: { toDesignationId: string; effectiveDate?: string; remarks?: string }
  ) => {
    try {
      const response = await apiClient.post(`/employees/${id}/promote`, payload);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  transfer: async (
    id: string,
    payload: { toBranchId: string; effectiveDate?: string; remarks?: string }
  ) => {
    try {
      const response = await apiClient.post(`/employees/${id}/transfer`, payload);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// CLASSES API
// Backend routes: /academic/classes*
// ============================================================================
export const classesApi = {
  getAll: async (params?: any) => {
    try {
      const response = await apiClient.get('/academic/classes', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getById: async (id: string) => {
    try {
      const response = await apiClient.get(`/academic/classes/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getSections: async (classId: string) => {
    try {
      const response = await apiClient.get(`/academic/classes/${classId}/sections`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  create: async (data: any) => {
    try {
      const response = await apiClient.post('/academic/classes', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  update: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/academic/classes/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  delete: async (id: string) => {
    try {
      const response = await apiClient.delete(`/academic/classes/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createSection: async (classId: string, data: any) => {
    try {
      const response = await apiClient.post(`/academic/classes/${classId}/sections`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateSection: async (sectionId: string, data: any) => {
    try {
      const response = await apiClient.put(`/academic/sections/${sectionId}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteSection: async (sectionId: string) => {
    try {
      const response = await apiClient.delete(`/academic/sections/${sectionId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// SECTIONS API
// Backend routes: /academic/classes/:id/sections
// ============================================================================
export const sectionsApi = {
  getAll: async (classId?: string) => {
    try {
      if (classId) {
        const response = await apiClient.get(`/academic/classes/${classId}/sections`);
        return response.data;
      }
      // Fallback: when no classId, fetch all sections across classes
      const response = await apiClient.get('/academic/classes');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getByClass: async (classId: string) => {
    try {
      const response = await apiClient.get(`/academic/classes/${classId}/sections`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getById: async (id: string) => {
    try {
      const response = await apiClient.get(`/academic/sections/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// SUBJECTS API
// Backend routes: /academic/subjects*
// ============================================================================
export const subjectsApi = {
  getAll: async (params?: { category?: string; type?: string }) => {
    try {
      const response = await apiClient.get('/academic/subjects', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getById: async (id: string) => {
    try {
      const response = await apiClient.get(`/academic/subjects/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getByClass: async (classId: string) => {
    try {
      const response = await apiClient.get(`/academic/subjects/class/${classId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  create: async (data: any) => {
    try {
      const response = await apiClient.post('/academic/subjects', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  update: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/academic/subjects/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  delete: async (id: string) => {
    try {
      const response = await apiClient.delete(`/academic/subjects/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// EXAMS API
// Backend routes: /exams/*
// ============================================================================
export const examsApi = {
  // Exam CRUD
  getAll: async (params?: { page?: number; limit?: number; type?: string; status?: string }) => {
    try {
      const response = await apiClient.get('/exams', { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },

  getHallTickets: async (params?: {
    examId?: string;
    classId?: string;
    sectionId?: string;
    status?: 'generated' | 'pending' | 'error';
    autoGenerate?: boolean;
    limitExams?: number;
  }) => {
    try {
      const response = await apiClient.get('/exams/hall-tickets', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  generateHallTickets: async (
    examId: string,
    data?: { classId?: string; sectionId?: string; centerName?: string },
  ) => {
    try {
      const response = await apiClient.post(`/exams/${examId}/hall-tickets/generate`, data || {});
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getById: async (id: string) => {
    try {
      const response = await apiClient.get(`/exams/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  create: async (data: any) => {
    try {
      const response = await apiClient.post('/exams', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  update: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/exams/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  delete: async (id: string) => {
    try {
      const response = await apiClient.delete(`/exams/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Class Mapping
  mapClasses: async (examId: string, classes: { class_id: string; section_id?: string }[]) => {
    try {
      const response = await apiClient.post(`/exams/${examId}/classes`, { classes });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getClassMappings: async (examId: string) => {
    try {
      const response = await apiClient.get(`/exams/${examId}/classes`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Subject Config
  configureSubjects: async (examId: string, classId: string, subjects: any[]) => {
    try {
      const response = await apiClient.post(`/exams/${examId}/classes/${classId}/subjects`, { subjects });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getSubjectConfigs: async (examId: string, classId: string) => {
    try {
      const response = await apiClient.get(`/exams/${examId}/classes/${classId}/subjects`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Grade Scales
  getGradeScales: async () => {
    try {
      const response = await apiClient.get('/exams/grade-scales');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createGradeScale: async (data: any) => {
    try {
      const response = await apiClient.post('/exams/grade-scales', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateGradeScale: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/exams/grade-scales/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteGradeScale: async (id: string) => {
    try {
      const response = await apiClient.delete(`/exams/grade-scales/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Results
  calculateResults: async (examId: string, classId: string, sectionId?: string) => {
    try {
      const response = await apiClient.post(`/exams/${examId}/results/calculate`, null, {
        params: { classId, sectionId },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  publishResults: async (
    examId: string,
    options?: {
      notifyStudents?: boolean;
      notifyParents?: boolean;
      notifySms?: boolean;
      notifyEmail?: boolean;
    },
  ) => {
    try {
      const response = await apiClient.put(`/exams/${examId}/results/publish`, options || {});
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getTNComplianceReport: async (examId: string) => {
    try {
      const response = await apiClient.get(`/exams/${examId}/tn-compliance-report`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Analysis
  getPerformanceAnalysis: async (examId: string, classId: string) => {
    try {
      const response = await apiClient.get(`/exams/${examId}/analysis/performance`, { params: { classId } });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getSubjectAnalysis: async (examId: string, subjectId: string) => {
    try {
      const response = await apiClient.get(`/exams/${examId}/analysis/subject`, { params: { subjectId } });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getToppers: async (examId: string, classId: string, limit?: number) => {
    try {
      const response = await apiClient.get(`/exams/${examId}/analysis/toppers`, { params: { classId, limit } });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getClassWiseResults: async (examId: string) => {
    try {
      const response = await apiClient.get(`/exams/${examId}/analysis/class-wise`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Marks anomalies (Phase 2 W6) ─────────────────────────────────────
  anomalies: {
    scan: async (
      examId: string,
      params?: { classId?: string; sectionId?: string; subjectId?: string },
    ) => {
      try {
        const response = await apiClient.post(
          `/exams/${examId}/anomalies/scan`,
          null,
          { params },
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    list: async (
      examId: string,
      status: 'open' | 'reviewed' | 'dismissed' | 'all' = 'open',
    ) => {
      try {
        const response = await apiClient.get(`/exams/${examId}/anomalies`, {
          params: { status },
        });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    review: async (
      anomalyId: string,
      payload: { status: 'reviewed' | 'dismissed'; notes?: string },
    ) => {
      try {
        const response = await apiClient.put(
          `/exams/anomalies/${anomalyId}/review`,
          payload,
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ── TN Compliance reports (Phase 2 W6) ───────────────────────────────
  tnCompliance: {
    report: async (examId: string) => {
      try {
        const response = await apiClient.get(
          `/exams/reports/tn-compliance/exam/${examId}`,
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    consolidatedMarkSheet: async (params: {
      examId: string;
      classId: string;
      sectionId?: string;
    }) => {
      try {
        const response = await apiClient.get(
          '/exams/reports/tn-compliance/consolidated-mark-sheet',
          { params },
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },
};

// ============================================================================
// MARKS API
// Backend routes: /exams/:examId/marks
// ============================================================================
export const marksApi = {
  getByExamAndClass: async (
    examId: string,
    classId: string,
    sectionId?: string,
    subjectId?: string,
  ) => {
    try {
      const response = await apiClient.get(`/exams/${examId}/marks`, {
        params: { classId, sectionId, subjectId },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  save: async (examId: string, data: { subject_id: string; class_id: string; section_id: string; marks: any[] }) => {
    try {
      const response = await apiClient.post(`/exams/${examId}/marks`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  lock: async (examId: string, classId: string, subjectId: string) => {
    try {
      const response = await apiClient.put(`/exams/${examId}/marks/lock`, null, {
        params: { classId, subjectId },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// ATTENDANCE API
// Backend routes: /attendance/*
// ============================================================================
export const attendanceApi = {
  getByDate: async (
    date: string,
    classId?: string,
    sectionId?: string,
  ) => {
    try {
      const response = await apiClient.get('/attendance/student', {
        params: { date, classId, sectionId },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  mark: async (data: {
    date: string;
    records: { studentId: string; status: string; remarks?: string }[];
  }) => {
    try {
      const response = await apiClient.post('/attendance/student/mark', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  markBulk: async (data: {
    date: string;
    classId: string;
    sectionId: string;
    records: any[];
  }) => {
    try {
      const response = await apiClient.post('/attendance/student/bulk', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  dailySummary: async (date: string) => {
    try {
      const response = await apiClient.get('/attendance/reports/daily', {
        params: { date },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getMonthlyReport: async (
    classId: string,
    sectionId: string,
    month: number,
    year: number,
  ) => {
    try {
      const response = await apiClient.get('/attendance/reports/monthly', {
        params: { classId, sectionId, month, year },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  monthlyReport: async (params: {
    classId: string;
    sectionId: string;
    month: number;
    year: number;
  }) => {
    try {
      const response = await apiClient.get('/attendance/reports/monthly', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getAbsenteeReport: async (date: string, classId?: string, sectionId?: string) => {
    try {
      const response = await apiClient.get('/attendance/reports/absentee', {
        params: { date, classId, sectionId },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getAbstractReport: async (params: { startDate: string; endDate: string; classId?: string; sectionId?: string }) => {
    try {
      const response = await apiClient.get('/attendance/reports/abstract', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getStudentHistory: async (studentId: string, params?: { startDate?: string; endDate?: string }) => {
    try {
      const response = await apiClient.get(`/attendance/reports/student/${studentId}`, { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getStaffAttendance: async (date: string) => {
    try {
      const response = await apiClient.get('/attendance/staff', { params: { date } });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  markStaffAttendance: async (data: { date: string; records: any[] }) => {
    try {
      const response = await apiClient.post('/attendance/staff/mark', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getLeaveTypes: async () => {
    try {
      const response = await apiClient.get('/attendance/leave/types');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createLeaveType: async (data: any) => {
    try {
      const response = await apiClient.post('/attendance/leave/types', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateLeaveType: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/attendance/leave/types/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteLeaveType: async (id: string) => {
    try {
      const response = await apiClient.delete(`/attendance/leave/types/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getLeaveRequests: async (params?: { status?: string; requesterId?: string }) => {
    try {
      const response = await apiClient.get('/attendance/leave/requests', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createLeaveRequest: async (data: any) => {
    try {
      const response = await apiClient.post('/attendance/leave/requests', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateLeaveRequest: async (id: string, data: { status: string; remarks?: string }) => {
    try {
      const response = await apiClient.put(`/attendance/leave/requests/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getLeaveBalance: async (userId: string) => {
    try {
      const response = await apiClient.get(`/attendance/leave/balance/${userId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Period attendance (W7) ──────────────────────────────────────────────

  markPeriod: async (data: {
    date: string;
    classId: string;
    sectionId?: string | null;
    periodId: string;
    students: { studentId: string; status: 'P' | 'A' | 'L' | 'M'; remarks?: string }[];
  }) => {
    try {
      const response = await apiClient.post('/attendance/period', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getPeriodRoster: async (
    classId: string,
    sectionId: string,
    date: string,
  ) => {
    try {
      const response = await apiClient.get(
        `/attendance/class/${classId}/section/${sectionId}/period-roster`,
        { params: { date } },
      );
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Chronic absentees v2 (W7) ───────────────────────────────────────────

  getChronicAbsenteesV2: async (params?: {
    classId?: string;
    sectionId?: string;
    asOfDate?: string;
    lookbackDays?: number;
  }) => {
    try {
      const response = await apiClient.get(
        '/attendance/reports/chronic-absentees/v2',
        { params },
      );
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Mid-day Meal Register (W7) ──────────────────────────────────────────

  mdm: {
    markRegister: async (data: {
      classId: string;
      date: string;
      totalPresent: number;
      mealType: 'breakfast' | 'lunch' | 'snack';
      mealComponents?: unknown[];
      signedByTeacher?: string | null;
      photoUrl?: string | null;
    }) => {
      try {
        const response = await apiClient.post('/attendance/midday-meal/register', data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    getDraft: async (classId: string, date: string) => {
      try {
        const response = await apiClient.get('/attendance/midday-meal/register', {
          params: { classId, date },
        });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    report: async (params: { from: string; to: string; classId?: string }) => {
      try {
        const response = await apiClient.get('/attendance/reports/midday-meal', {
          params,
        });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ── Medical exemptions (W7) ─────────────────────────────────────────────

  medicalExemptions: {
    create: async (data: {
      studentId: string;
      fromDate: string;
      toDate: string;
      reason: string;
      certificateUrl?: string | null;
      notes?: string | null;
    }) => {
      try {
        const response = await apiClient.post(
          '/attendance/medical-exemptions',
          data,
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    list: async (params?: {
      studentId?: string;
      status?: 'pending' | 'approved' | 'rejected';
    }) => {
      try {
        const response = await apiClient.get(
          '/attendance/medical-exemptions',
          { params },
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    approve: async (
      id: string,
      data: { decision: 'approved' | 'rejected'; notes?: string | null },
    ) => {
      try {
        const response = await apiClient.put(
          `/attendance/medical-exemptions/${id}/approve`,
          data,
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ── Biometric devices (W7) ──────────────────────────────────────────────

  getBiometricSyncLogs: async (deviceId: string, limit = 50) => {
    try {
      const response = await apiClient.get(`/attendance/biometric/logs/${deviceId}`, {
        params: { limit },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getBiometricStats: async (deviceId: string, days = 30) => {
    try {
      const response = await apiClient.get(`/attendance/biometric/stats/${deviceId}`, {
        params: { days },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── E4: Academic Calendar ──────────────────────────────────────────────
  calendar: {
    list: async (academicYearId?: string) => {
      try {
        const response = await apiClient.get('/attendance/calendar/events', {
          params: { academicYearId },
        });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    create: async (data: {
      academicYearId: string;
      eventDate: string;
      eventType: string;
      name: string;
      nameTamil?: string | null;
      appliesToClasses?: string[] | null;
      isAttendanceRequired?: boolean;
    }) => {
      try {
        const response = await apiClient.post('/attendance/calendar/events', data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    remove: async (id: string) => {
      try {
        const response = await apiClient.delete(`/attendance/calendar/events/${id}`);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    bulkImport: async (data: {
      academicYearId: string;
      rows: Array<{ date: string; event_type: string; name: string; name_tamil?: string }>;
    }) => {
      try {
        const response = await apiClient.post('/attendance/calendar/events/bulk-import', data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    workingDays: async (academicYearId: string, from: string, to: string) => {
      try {
        const response = await apiClient.get('/attendance/calendar/working-days', {
          params: { academicYearId, from, to },
        });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ── E4: Late-Entry Rules ───────────────────────────────────────────────
  lateRules: {
    list: async () => {
      try {
        const response = await apiClient.get('/attendance/late-rules');
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    upsert: async (data: {
      classId?: string | null;
      lateAfterMinutes: number;
      halfDayAfterMinutes: number;
      halfDayMorningStart: string;
      halfDayAfternoonStart: string;
    }) => {
      try {
        const response = await apiClient.post('/attendance/late-rules', data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    markWithTime: async (data: {
      studentId: string;
      date: string;
      arrivalTime: string;
      classId: string;
      sectionId?: string | null;
      academicYearId?: string | null;
    }) => {
      try {
        const response = await apiClient.post('/attendance/late-entry/mark', data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    history: async (studentId: string, academicYearId?: string) => {
      try {
        const response = await apiClient.get(
          `/attendance/late-entry/history/${studentId}`,
          { params: { academicYearId } },
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ── E4: Outdoor Exemptions ─────────────────────────────────────────────
  outdoorExemptions: {
    list: async (params?: { studentId?: string; from?: string; to?: string }) => {
      try {
        const response = await apiClient.get('/attendance/outdoor-exemptions', { params });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    request: async (data: {
      studentIds: string[];
      groupKind: string;
      activityName: string;
      fromDate: string;
      toDate: string;
      supervisorEmployeeId?: string | null;
      supportingDocumentUrl?: string | null;
      exemptFromAttendance?: boolean;
      exemptFromClasses?: boolean;
    }) => {
      try {
        const response = await apiClient.post('/attendance/outdoor-exemptions', data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    active: async (date?: string) => {
      try {
        const response = await apiClient.get('/attendance/outdoor-exemptions/active', {
          params: { date },
        });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ── E4: Mass-Bunk Detection ────────────────────────────────────────────
  massBunk: {
    forDay: async (date?: string) => {
      try {
        const response = await apiClient.get('/attendance/mass-bunk', { params: { date } });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ── E4: Early-Leave Sign-Out ───────────────────────────────────────────
  signouts: {
    list: async (params?: { date?: string; studentId?: string; parentVerified?: boolean }) => {
      try {
        const response = await apiClient.get('/attendance/signouts', { params });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    signout: async (data: {
      studentId: string;
      date: string;
      signoutTime: string;
      pickedUpByGuardianId?: string | null;
      pickedUpByOtherName?: string | null;
      pickedUpByOtherIdProof?: string | null;
      reason: string;
      photoUrl?: string | null;
    }) => {
      try {
        const response = await apiClient.post('/attendance/signouts', data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    verify: async (id: string) => {
      try {
        const response = await apiClient.post(`/attendance/signouts/${id}/verify`);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ── E4: Subject-Wise Attendance ────────────────────────────────────────
  subjectAttendance: {
    forStudent: async (studentId: string, academicYearId?: string) => {
      try {
        const response = await apiClient.get(`/attendance/subjects/student/${studentId}`, {
          params: { academicYearId },
        });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    forClass: async (classId: string, academicYearId: string) => {
      try {
        const response = await apiClient.get(`/attendance/subjects/class/${classId}`, {
          params: { academicYearId },
        });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    recompute: async (academicYearId: string) => {
      try {
        const response = await apiClient.post('/attendance/subjects/recompute', {
          academicYearId,
        });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ── E4: Geofence ───────────────────────────────────────────────────────
  geofence: {
    listZones: async () => {
      try {
        const response = await apiClient.get('/attendance/geofence/zones');
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    createZone: async (data: { name: string; lat: number; lng: number; radius?: number }) => {
      try {
        const response = await apiClient.post('/attendance/geofence/zones', data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    updateZone: async (
      id: string,
      data: { name?: string; lat?: number; lng?: number; radius?: number; isActive?: boolean },
    ) => {
      try {
        const response = await apiClient.put(`/attendance/geofence/zones/${id}`, data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    selfMarkIn: async (data: {
      studentId?: string;
      date?: string;
      lat: number;
      lng: number;
      classId?: string;
      sectionId?: string;
      academicYearId?: string;
    }) => {
      try {
        const response = await apiClient.post('/attendance/geofence/self-mark-in', data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },
};

// ============================================================================
// FEES API
// Backend routes: /fees/*
// ============================================================================
export const feesApi = {
  getStructures: async (params?: any) => {
    try {
      const response = await apiClient.get('/fees/structures', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createStructure: async (data: any) => {
    try {
      const response = await apiClient.post('/fees/structures', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getStudentFees: async (params?: {
    page?: number;
    limit?: number;
    classId?: string;
    status?: string;
  }) => {
    try {
      const response = await apiClient.get('/fees/student-ledger', { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },

  getStudentLedger: async (studentId?: string, params?: any) => {
    try {
      if (studentId) {
        const response = await apiClient.get(`/fees/student-ledger/${studentId}`, { params });
        return response.data;
      }
      const response = await apiClient.get('/fees/student-ledger', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  collectFee: async (data: {
    studentId: string;
    amount: number;
    paymentMode: string;
    feeHeads: Array<{ head?: string; feeHeadId?: string; feeHeadName?: string; amount: number }>;
    discount?: number;
    discountReason?: string;
    // ── W5: structured cheque + partial flag ────────────────────────
    cheque?: { no: string; date: string; bank: string; branch: string };
    isPartial?: boolean;
    // Legacy flat cheque fields kept for back-compat
    chequeNumber?: string;
    chequeDate?: string;
    bankName?: string;
    bankBranch?: string;
    transactionRefId?: string;
    cardLast4?: string;
    upiId?: string;
    // Optional extras
    remarks?: string;
    reference?: string;
    split?: { mode: string; amount: number };
  }) => {
    try {
      const response = await apiClient.post('/fees/collect', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── W5: Offline-queue sync ─────────────────────────────────────────
  syncOfflineQueue: async (
    items: Array<{
      clientId?: string;
      studentId: string;
      amount: number;
      paymentMode: string;
      feeHeads: Array<{ head?: string; feeHeadId?: string; feeHeadName?: string; amount: number }>;
      capturedAt: string;
      cheque?: { no: string; date: string; bank: string; branch: string };
      isPartial?: boolean;
      reference?: string;
      remarks?: string;
    }>,
  ) => {
    try {
      const response = await apiClient.post('/fees/offline-queue/sync', { items });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── W5: YoY cap preview (used by FeeStructure dialog) ──────────────
  previewCap: async (structureId: string, newAmount: number) => {
    try {
      const response = await apiClient.post(
        `/fees/structures/${structureId}/preview-cap`,
        { newAmount },
      );
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── W5: Defaulters aging buckets (mv_fee_defaulters_daily) ────────
  getDefaultersAging: async () => {
    try {
      const response = await apiClient.get('/fees/defaulters/aging');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── W5: Send a defaulter reminder via the comms pipeline ──────────
  // Calls /fees/reminders/send (backend route to be wired in W5b);
  // FeeDefaulters falls back to smsApi/emailApi at the call-site when
  // the route is not yet available.
  sendReminder: async (studentId: string, channel: 'sms' | 'whatsapp' | 'email') => {
    try {
      const response = await apiClient.post('/fees/reminders/send', {
        studentId,
        channel,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  cancelBill: async (id: string, reason: string) => {
    try {
      const response = await apiClient.post(`/fees/receipts/${id}/cancel`, { reason });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getReceipt: async (id: string) => {
    try {
      const response = await apiClient.get(`/fees/receipts/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getDefaulters: async (params?: { page?: number; limit?: number; minDays?: number }) => {
    try {
      const response = await apiClient.get('/fees/defaulters', { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },

  getTransactions: async (params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    // Backend filter names (fee.controller getTransactions):
    fromDate?: string;
    toDate?: string;
    studentId?: string;
    status?: string;
    paymentMode?: string;
    type?: string;
  }) => {
    try {
      const response = await apiClient.get('/fees/transactions', { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },

  getDailyCollection: async (date: string) => {
    try {
      const response = await apiClient.get('/fees/reports/daily-collection', {
        params: { date },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getCollectionReport: async (params?: any) => {
    try {
      const response = await apiClient.get('/fees/reports/collected', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getCancelledBills: async (params?: any) => {
    try {
      const response = await apiClient.get('/fees/reports/cancelled', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getAbstractReport: async (params?: any) => {
    try {
      const response = await apiClient.get('/fees/reports/abstract', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getConcessions: async (params?: any) => {
    try {
      const response = await apiClient.get('/fees/concessions', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createConcession: async (data: any) => {
    try {
      const response = await apiClient.post('/fees/concessions', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateConcession: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/fees/concessions/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteConcession: async (id: string) => {
    try {
      const response = await apiClient.delete(`/fees/concessions/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getStructureById: async (id: string) => {
    try {
      const response = await apiClient.get(`/fees/structures/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateStructure: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/fees/structures/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteStructure: async (id: string) => {
    try {
      const response = await apiClient.delete(`/fees/structures/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  assignStructure: async (data: { studentId: string; feeStructureId: string; academicYearId: string }) => {
    try {
      const response = await apiClient.post('/fees/assign-structure', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getCategories: async (params?: any) => {
    try {
      const response = await apiClient.get('/fees/categories', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createCategory: async (data: any) => {
    try {
      const response = await apiClient.post('/fees/categories', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  duplicateReceipt: async (id: string) => {
    try {
      const response = await apiClient.post(`/fees/receipts/${id}/duplicate`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getDueReport: async (params?: any) => {
    try {
      const response = await apiClient.get('/fees/reports/due', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getConcessionReport: async (params?: any) => {
    try {
      const response = await apiClient.get('/fees/reports/concession', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Receipt Templates
  getReceiptTemplates: async (params?: any) => {
    try {
      const response = await apiClient.get('/fees/receipt-templates', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createReceiptTemplate: async (data: any) => {
    try {
      const response = await apiClient.post('/fees/receipt-templates', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateReceiptTemplate: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/fees/receipt-templates/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  setDefaultTemplate: async (id: string) => {
    try {
      const response = await apiClient.put(`/fees/receipt-templates/${id}/set-default`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Bulk Assign Structure
  bulkAssignStructure: async (data: {
    classId: string;
    sectionId?: string;
    academicYearId: string;
    feeStructureId: string;
  }) => {
    try {
      const response = await apiClient.post('/fees/bulk-assign-structure', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Fee Analytics & Predictions
  getDefaultersWithRiskScores: async (params?: { limit?: number }) => {
    try {
      const response = await apiClient.get('/fees/analytics/defaulters/risk-score', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getStudentRiskScore: async (studentId: string) => {
    try {
      const response = await apiClient.get(`/fees/analytics/student/${studentId}/risk-score`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getPaymentPrediction: async (studentId: string) => {
    try {
      const response = await apiClient.get(`/fees/analytics/student/${studentId}/payment-prediction`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getPredictedCollection: async (params?: { days?: number }) => {
    try {
      const response = await apiClient.get('/fees/analytics/predicted-collection', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Wave 1e: Receipt PDF, GL-lite, Bank Reconcile, Concession Engine ──
  downloadReceiptPdf: async (txnId: string, opts?: { duplicate?: boolean }): Promise<Blob> => {
    const response = await apiClient.get(`/fees/receipts/${txnId}/pdf`, {
      params: opts?.duplicate ? { duplicate: 1 } : undefined,
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  getTrialBalance: async (from: string, to: string) => {
    try {
      const response = await apiClient.get('/fees/gl/trial-balance', {
        params: { from, to },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getProfitLoss: async (from: string, to: string) => {
    try {
      const response = await apiClient.get('/fees/gl/profit-loss', {
        params: { from, to },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getBalanceSheet: async (asOf: string) => {
    try {
      const response = await apiClient.get('/fees/gl/balance-sheet', {
        params: { asOf },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getCashFlow: async (from: string, to: string) => {
    try {
      const response = await apiClient.get('/fees/gl/cash-flow', {
        params: { from, to },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getGlReconciliation: async (from: string, to: string) => {
    try {
      const response = await apiClient.get('/fees/gl/reconciliation', {
        params: { from, to },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getBankReconciliationStatus: async () => {
    try {
      const response = await apiClient.get('/fees/gl/bank-reconciliation-status');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getGstSummary: async (from: string, to: string) => {
    try {
      const response = await apiClient.get('/fees/gl/gst-summary', {
        params: { from, to },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  postManualJournal: async (payload: {
    entryDate: string;
    narration: string;
    lines: Array<{ accountCode: string; debit?: number; credit?: number; description?: string }>;
  }) => {
    try {
      const response = await apiClient.post('/fees/gl/journal', payload);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  yearEndClose: async (payload: { fiscalYearStart: string; fiscalYearEnd: string }) => {
    try {
      const response = await apiClient.post('/fees/gl/year-end-close', payload);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getChartOfAccounts: async () => {
    try {
      const response = await apiClient.get('/fees/gl/chart-of-accounts');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  reconcileBank: async (formData: FormData) => {
    try {
      const response = await apiClient.post('/fees/gl/bank-reconcile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  evaluateConcessions: async (studentId: string) => {
    try {
      const response = await apiClient.get(`/fees/concessions/evaluate/${studentId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Wave 1e-continued: Finance Dashboard + Receipt Archive helpers ──
  /**
   * Finance dashboard aggregate: totals, trend, class breakdown, defaulters.
   * Backend endpoint may not exist — we defensively aggregate from the
   * legacy endpoints that DO exist so the page always renders.
   */
  getDashboardStats: async () => {
    try {
      // Try the dedicated endpoint first
      const response = await apiClient.get('/fees/dashboard/stats');
      return response.data;
    } catch {
      // Fallback: synthesise from existing endpoints
      try {
        const [daily, collection, defaulters] = await Promise.all([
          apiClient.get('/fees/reports/daily-collection', {
            params: { date: new Date().toISOString().split('T')[0] },
          }).catch(() => ({ data: null })),
          apiClient.get('/fees/reports/collected').catch(() => ({ data: null })),
          apiClient.get('/fees/defaulters', { params: { limit: 5 } }).catch(() => ({ data: null })),
        ]);
        return {
          success: true,
          data: {
            daily: (daily as any)?.data?.data ?? (daily as any)?.data ?? null,
            collection: (collection as any)?.data?.data ?? (collection as any)?.data ?? null,
            defaulters: (defaulters as any)?.data?.data ?? (defaulters as any)?.data ?? [],
          },
        };
      } catch (error: any) {
        return handleError(error);
      }
    }
  },

  /**
   * Email a receipt to the student's guardian.
   * Falls back to the generic email endpoint if no dedicated route exists.
   */
  emailReceipt: async (txnId: string, email?: string) => {
    try {
      const response = await apiClient.post(`/fees/receipts/${txnId}/email`, { email });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  /**
   * Bulk evaluate concessions across students.
   */
  bulkEvaluateConcessions: async (studentIds: string[]) => {
    try {
      const response = await apiClient.post('/fees/concessions/evaluate-bulk', { studentIds });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  /**
   * Preview auto-applicable concessions BEFORE a student record exists.
   * Called from the AdmissionWizard Step 4 to show fee deductions in real-time.
   */
  previewConcessions: async (body: {
    classId: string;
    academicYearId?: string;
    category: string;
    admissionType: string;
    siblingGroupId?: string;
  }) => {
    try {
      const response = await apiClient.post('/fees/concessions/preview', body);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  /**
   * Export student ledger as PDF.
   */
  exportStudentLedger: async (studentId: string): Promise<Blob | null> => {
    try {
      const response = await apiClient.get(`/fees/student-ledger/${studentId}/pdf`, {
        responseType: 'blob',
      });
      return response.data as Blob;
    } catch {
      return null;
    }
  },

  // ──────────────────────────────────────────────────────────────────
  // E2 — Term-installments
  // ──────────────────────────────────────────────────────────────────
  terms: {
    list: async (academicYearId: string) => {
      try {
        const response = await apiClient.get('/fees/terms', {
          params: { academicYearId },
        });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    create: async (data: {
      academicYearId: string;
      name: string;
      termNo: number;
      startDate?: string;
      endDate?: string;
      dueDate?: string;
    }) => {
      try {
        const response = await apiClient.post('/fees/terms', data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    update: async (id: string, data: Record<string, unknown>) => {
      try {
        const response = await apiClient.put(`/fees/terms/${id}`, data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    seedDefaults: async (academicYearId: string) => {
      try {
        const response = await apiClient.post('/fees/terms/seed-defaults', {
          academicYearId,
        });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    splitStructure: async (
      structureId: string,
      splits: Array<{
        termNo: number;
        amount: number;
        lateFeeAfterDueDate?: string;
      }>,
    ) => {
      try {
        const response = await apiClient.post(
          `/fees/structures/${structureId}/split-terms`,
          { splits },
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    studentSchedule: async (studentId: string, academicYearId: string) => {
      try {
        const response = await apiClient.get(
          `/fees/students/${studentId}/installment-schedule`,
          { params: { academicYearId } },
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // E2 — Fee bundles
  // ──────────────────────────────────────────────────────────────────
  bundles: {
    list: async () => {
      try {
        const response = await apiClient.get('/fees/bundles');
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    get: async (id: string) => {
      try {
        const response = await apiClient.get(`/fees/bundles/${id}`);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    create: async (data: {
      name: string;
      description?: string;
      applicableClassIds?: string[];
      applicableCategory?: string;
      components: Array<{
        feeHeadId: string;
        amount: number;
        isOptional?: boolean;
        displayOrder?: number;
      }>;
    }) => {
      try {
        const response = await apiClient.post('/fees/bundles', data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    update: async (id: string, data: Record<string, unknown>) => {
      try {
        const response = await apiClient.put(`/fees/bundles/${id}`, data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    delete: async (id: string) => {
      try {
        const response = await apiClient.delete(`/fees/bundles/${id}`);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    apply: async (
      bundleId: string,
      data: {
        studentId: string;
        academicYearId: string;
        customizations?: Record<
          string,
          { included: boolean; amount?: number }
        >;
      },
    ) => {
      try {
        const response = await apiClient.post(
          `/fees/bundles/${bundleId}/apply`,
          data,
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // E2 — Cheque bounce + clearance
  // ──────────────────────────────────────────────────────────────────
  chequeBounce: {
    list: async (status?: 'open' | 'resolved') => {
      try {
        const response = await apiClient.get('/fees/cheques/bounces', {
          params: status ? { status } : undefined,
        });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    markCleared: async (transactionId: string) => {
      try {
        const response = await apiClient.put(
          `/fees/cheques/${transactionId}/mark-cleared`,
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    markBounced: async (
      transactionId: string,
      data: { bounceReason: string; bankCharge?: number },
    ) => {
      try {
        const response = await apiClient.put(
          `/fees/cheques/${transactionId}/mark-bounced`,
          data,
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    resolve: async (
      bounceId: string,
      data: { method: string; replacementTransactionId?: string },
    ) => {
      try {
        const response = await apiClient.put(
          `/fees/cheques/bounces/${bounceId}/resolve`,
          data,
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    notify: async (bounceId: string) => {
      try {
        const response = await apiClient.put(
          `/fees/cheques/bounces/${bounceId}/notify`,
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // E2 — Refund management
  // ──────────────────────────────────────────────────────────────────
  refunds: {
    list: async (filter?: { status?: string; studentId?: string }) => {
      try {
        const response = await apiClient.get('/fees/refunds', {
          params: filter,
        });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    get: async (id: string) => {
      try {
        const response = await apiClient.get(`/fees/refunds/${id}`);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    request: async (data: {
      studentId: string;
      amount?: number;
      reason: string;
      refundMode: string;
      originalTransactionIds?: string[];
      bankAccountNo?: string;
      ifsc?: string;
      beneficiaryName?: string;
      notes?: string;
      academicYearId?: string;
    }) => {
      try {
        const response = await apiClient.post('/fees/refunds', data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    approve: async (id: string) => {
      try {
        const response = await apiClient.put(`/fees/refunds/${id}/approve`);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    reject: async (id: string, reason: string) => {
      try {
        const response = await apiClient.put(`/fees/refunds/${id}/reject`, {
          reason,
        });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    markPaid: async (id: string) => {
      try {
        const response = await apiClient.put(`/fees/refunds/${id}/mark-paid`);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    cancel: async (id: string) => {
      try {
        const response = await apiClient.put(`/fees/refunds/${id}/cancel`);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    previewProRata: async (studentId: string, academicYearId?: string) => {
      try {
        const response = await apiClient.get(
          `/fees/refunds/preview/pro-rata/${studentId}`,
          { params: academicYearId ? { academicYearId } : undefined },
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // E2 — Year rollover
  // ──────────────────────────────────────────────────────────────────
  rollover: {
    preview: async (data: { fromAyId: string; toAyId: string }) => {
      try {
        const response = await apiClient.post('/fees/rollover/preview', data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    execute: async (data: {
      fromAyId: string;
      toAyId: string;
      increasePct?: number;
      notes?: string;
    }) => {
      try {
        const response = await apiClient.post('/fees/rollover/execute', data);
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    revert: async (runId: string) => {
      try {
        const response = await apiClient.put(
          `/fees/rollover/${runId}/revert`,
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    listRuns: async () => {
      try {
        const response = await apiClient.get('/fees/rollover/runs');
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // E2 — Concession stacking
  // ──────────────────────────────────────────────────────────────────
  concessionStack: {
    evaluate: async (studentId: string, academicYearId?: string) => {
      try {
        const response = await apiClient.get(
          `/fees/concessions/stack/${studentId}`,
          { params: academicYearId ? { academicYearId } : undefined },
        );
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    getPriority: async () => {
      try {
        const response = await apiClient.get('/fees/concessions/priority');
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
    setPriority: async (priority: string[]) => {
      try {
        const response = await apiClient.put('/fees/concessions/priority', {
          priority,
        });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },
};

// ============================================================================
// HR API — Performance Appraisals + Recruitment
// Backend routes: /hr/*
// ============================================================================
export const performanceApi = {
  listCycles: async () => {
    try {
      const response = await apiClient.get('/hr/appraisal/cycles');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  createCycle: async (data: any) => {
    try {
      const response = await apiClient.post('/hr/appraisal/cycles', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  updateCycleStatus: async (id: string, status: string) => {
    try {
      const response = await apiClient.put(`/hr/appraisal/cycles/${id}/status`, { status });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  listTemplates: async () => {
    try {
      const response = await apiClient.get('/hr/appraisal/templates');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  createTemplate: async (data: any) => {
    try {
      const response = await apiClient.post('/hr/appraisal/templates', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  listReviews: async (params?: { cycleId?: string; employeeId?: string; status?: string }) => {
    try {
      const response = await apiClient.get('/hr/appraisal/reviews', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  initiateReview: async (data: any) => {
    try {
      const response = await apiClient.post('/hr/appraisal/reviews', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  submitReview: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/hr/appraisal/reviews/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  acknowledgeReview: async (id: string) => {
    try {
      const response = await apiClient.put(`/hr/appraisal/reviews/${id}/acknowledge`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

export const recruitmentApi = {
  listJobs: async (params?: { status?: string; departmentId?: string; search?: string; page?: number }) => {
    try {
      const response = await apiClient.get('/hr/recruitment/jobs', { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },
  createJob: async (data: any) => {
    try {
      const response = await apiClient.post('/hr/recruitment/jobs', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  updateJob: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/hr/recruitment/jobs/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  updateJobStatus: async (id: string, status: string) => {
    try {
      const response = await apiClient.put(`/hr/recruitment/jobs/${id}/status`, { status });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  listCandidates: async (params?: { jobPostingId?: string; status?: string; stage?: number; search?: string; page?: number }) => {
    try {
      const response = await apiClient.get('/hr/recruitment/candidates', { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },
  createCandidate: async (data: any) => {
    try {
      const response = await apiClient.post('/hr/recruitment/candidates', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  updateCandidate: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/hr/recruitment/candidates/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  updateCandidateStage: async (id: string, stage: number, remarks?: string) => {
    try {
      const response = await apiClient.put(`/hr/recruitment/candidates/${id}/stage`, { stage, remarks });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  deleteCandidate: async (id: string) => {
    try {
      const response = await apiClient.delete(`/hr/recruitment/candidates/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// PAYROLL API
// Backend routes: /payroll/*
// ============================================================================
export const payrollApi = {
  // ---- Salary Components ----
  getSalaryComponents: async () => {
    try {
      const response = await apiClient.get('/payroll/salary-components');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createSalaryComponent: async (data: any) => {
    try {
      const response = await apiClient.post('/payroll/salary-components', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateSalaryComponent: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/payroll/salary-components/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ---- Salary Structures ----
  getSalaryStructures: async () => {
    try {
      const response = await apiClient.get('/payroll/salary-structures');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getSalaryStructureById: async (id: string) => {
    try {
      const response = await apiClient.get(`/payroll/salary-structures/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createSalaryStructure: async (data: any) => {
    try {
      const response = await apiClient.post('/payroll/salary-structures', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateSalaryStructure: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/payroll/salary-structures/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ---- Employee Assignments ----
  getEmployeeAssignments: async (params?: any) => {
    try {
      const response = await apiClient.get('/payroll/employee-assignments', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  assignSalaryToEmployee: async (data: any) => {
    try {
      const response = await apiClient.post('/payroll/employee-assignments', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateAssignment: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/payroll/employee-assignments/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ---- Processing ----
  previewPayroll: async (month: number, year: number) => {
    try {
      const response = await apiClient.post('/payroll/preview', { month, year });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  processPayroll: async (month: number, year: number) => {
    try {
      const response = await apiClient.post('/payroll/process', { month, year });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ---- Runs ----
  getRuns: async (params?: any) => {
    try {
      const response = await apiClient.get('/payroll/runs', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getRunById: async (id: string) => {
    try {
      const response = await apiClient.get(`/payroll/runs/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  approveRun: async (id: string) => {
    try {
      const response = await apiClient.put(`/payroll/runs/${id}/approve`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  publishRun: async (id: string) => {
    try {
      const response = await apiClient.put(`/payroll/runs/${id}/publish`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ---- W8: Unpublish + manual dispatch + compliance audit ----
  unpublish: async (id: string, reason: string) => {
    try {
      const response = await apiClient.put(`/payroll/runs/${id}/unpublish`, { reason });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  dispatch: async (runId: string) => {
    try {
      const response = await apiClient.post(`/payroll/runs/${runId}/dispatch`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  compliance: {
    audit: async (params?: {
      tableName?: string;
      page?: number;
      pageSize?: number;
      from?: string;
      to?: string;
    }) => {
      try {
        const response = await apiClient.get('/payroll/compliance/audit', { params });
        return response.data;
      } catch (error: any) {
        return handleError(error);
      }
    },
  },

  holdSalary: async (runId: string, employeeId: string, reason: string) => {
    try {
      const response = await apiClient.post(`/payroll/runs/${runId}/hold`, {
        employee_id: employeeId,
        reason,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  releaseSalary: async (runId: string, employeeId: string) => {
    try {
      const response = await apiClient.post(`/payroll/runs/${runId}/release`, {
        employee_id: employeeId,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ---- Payslip Distribution ----
  sendPayslip: async (payslipId: string) => {
    try {
      const response = await apiClient.post(`/payroll/payslips/${payslipId}/send`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ---- Reports ----
  getSalaryRegister: async (params?: any) => {
    try {
      const response = await apiClient.get('/payroll/reports/salary-register', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getDepartmentWiseReport: async (params?: any) => {
    try {
      const response = await apiClient.get('/payroll/reports/department-wise', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ---- TDS Compute / Form 16 / Salary Revisions (Wave 1d) ----
  computeTDS: async (body: { employeeId: string; month: number; year: number; regime: 'old' | 'new' }) => {
    try {
      const response = await apiClient.post('/payroll/tds/compute', body);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  downloadForm16: async (employeeId: string, fy: string): Promise<Blob | null> => {
    try {
      const response = await apiClient.get(`/payroll/tds/form-16/${employeeId}/${fy}`, {
        responseType: 'blob',
      });
      return response.data as Blob;
    } catch (error: any) {
      console.error('Form 16 download failed:', error);
      return null;
    }
  },

  createSalaryRevision: async (body: {
    employeeId: string;
    newStructureId: string;
    effectiveFrom: string;
    reason: string;
  }) => {
    try {
      const response = await apiClient.post('/payroll/salary-revisions', body);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  listSalaryRevisions: async (employeeId: string) => {
    try {
      const response = await apiClient.get(`/payroll/salary-revisions/${employeeId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ---- W7: Payroll & Tax config — unassigned-employee picker + bulk assign ----
  getUnassignedEmployees: async () => {
    try {
      const response = await apiClient.get('/payroll/employee-assignments/unassigned');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  bulkAssignSalary: async (data: {
    salaryStructureId: string;
    effectiveFrom: string;
    assignments: Array<{ employeeId: string; basicSalary: number; componentOverrides?: Record<string, number> }>;
  }) => {
    try {
      const response = await apiClient.post('/payroll/employee-assignments/bulk', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// ADMISSIONS / LEADS API
// Backend routes: /admission/*
// ============================================================================
export const leadsApi = {
  // Approval workflow
  getApprovalWorkflow: async () => {
    try {
      const response = await apiClient.get('/admission/workflow');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateApprovalWorkflow: async (data: any) => {
    try {
      const response = await apiClient.put('/admission/workflow', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Review-SLA summary for the admission queue / workflow-settings health card.
  // Returns { reviewSlaHours, counts: { pendingPastSla, byStage } }.
  getApplicationSlaSummary: async () => {
    try {
      const response = await apiClient.get('/admission/applications/sla-summary');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Lead pipeline configuration (stages, purpose, transitions, entry gates).
  getLeadPipelineConfig: async () => {
    try {
      const response = await apiClient.get('/admission/lead-pipeline-config');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateLeadPipelineConfig: async (data: any) => {
    try {
      const response = await apiClient.put('/admission/lead-pipeline-config', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Custom pipeline templates (admin-saved, re-appliable presets).
  getLeadPipelineTemplates: async () => {
    try {
      const response = await apiClient.get('/admission/lead-pipeline-templates');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  saveLeadPipelineTemplate: async (data: { name: string; description?: string; stages: any[] }) => {
    try {
      const response = await apiClient.post('/admission/lead-pipeline-templates', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  deleteLeadPipelineTemplate: async (id: string) => {
    try {
      const response = await apiClient.delete(`/admission/lead-pipeline-templates/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Custom approval-workflow templates.
  getWorkflowTemplates: async () => {
    try {
      const response = await apiClient.get('/admission/workflow-templates');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  saveWorkflowTemplate: async (data: { name: string; description?: string; stages: any[] }) => {
    try {
      const response = await apiClient.post('/admission/workflow-templates', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
  deleteWorkflowTemplate: async (id: string) => {
    try {
      const response = await apiClient.delete(`/admission/workflow-templates/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Lead CRUD
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    stage?: string;
    source?: string;
    priority?: string;
  }) => {
    try {
      const response = await apiClient.get('/admission/leads', { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },

  getAllWithDetails: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    stage?: string;
    source?: string;
    priority?: string;
  }) => {
    try {
      const response = await apiClient.get('/admission/leads/with-details', { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },

  getBoard: async () => {
    try {
      const response = await apiClient.get('/admission/leads/board');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getById: async (id: string) => {
    try {
      const response = await apiClient.get(`/admission/leads/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getLeadDuplicates: async (id: string) => {
    try {
      const response = await apiClient.get(`/admission/leads/${id}/duplicates`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  resolveLeadDuplicate: async (
    duplicateCaseId: string,
    action: 'merge' | 'link' | 'dismiss',
    note?: string,
  ) => {
    try {
      const response = await apiClient.put(`/admission/duplicates/${duplicateCaseId}/resolve`, {
        action,
        note,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  create: async (data: any) => {
    try {
      const response = await apiClient.post('/admission/leads', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  update: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/admission/leads/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  delete: async (id: string) => {
    try {
      const response = await apiClient.delete(`/admission/leads/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateStage: async (
    id: string,
    stage: string,
    notes?: string,
    options?: {
      lostReason?: string;
      reopenReason?: string;
      // Guided walk-in: supplied when a lead is dragged straight to Visited from
      // new/contacted. Backend logs the visit + moves stage in one transaction.
      walkInVisit?: { date?: string; outcome?: string; notes?: string };
    },
  ) => {
    try {
      const response = await apiClient.put(`/admission/leads/${id}/stage`, {
        stage,
        notes,
        lost_reason: options?.lostReason,
        reopen_reason: options?.reopenReason,
        walk_in_visit: options?.walkInVisit,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Lead intelligence (Wave A2) — speed-to-lead SLA, next-best-action,
  //    dormant segments. Batched by comma-separated ids.
  getSlaStatus: async (leadIds: string[]) => {
    try {
      const response = await apiClient.get('/admission/leads/sla-status', {
        params: { ids: leadIds.join(',') },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getNextActions: async (leadIds: string[]) => {
    try {
      const response = await apiClient.get('/admission/leads/next-actions', {
        params: { ids: leadIds.join(',') },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getDormantSegment: async (days: 30 | 90 | number = 30) => {
    try {
      const response = await apiClient.get('/admission/leads/segments/dormant', {
        params: { days },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Lead Documents
  getLeadDocuments: async (leadId: string) => {
    try {
      const response = await apiClient.get(`/admission/leads/${leadId}/documents`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  upsertLeadDocument: async (
    leadId: string,
    data: { document_key: string; document_name: string; file_url?: string },
  ) => {
    try {
      const response = await apiClient.put(`/admission/leads/${leadId}/documents`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  verifyLeadDocument: async (documentId: string, notes?: string) => {
    try {
      const response = await apiClient.put(`/admission/lead-documents/${documentId}/verify`, { notes });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  rejectLeadDocument: async (documentId: string, rejection_reason: string, rejection_notes?: string) => {
    try {
      const response = await apiClient.put(`/admission/lead-documents/${documentId}/reject`, {
        rejection_reason,
        rejection_notes,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Fee Waivers
  getLeadWaivers: async (leadId: string) => {
    try {
      const response = await apiClient.get(`/admission/leads/${leadId}/waivers`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  requestLeadWaiver: async (
    leadId: string,
    data: {
      application_id?: string;
      waiver_type: 'application_fee' | 'admission_fee';
      waiver_percentage: number;
      reason: 'financial_hardship' | 'sibling_discount' | 'staff_ward' | 'management_quota' | 'alumni' | 'other';
      additional_notes?: string;
    },
  ) => {
    try {
      const response = await apiClient.post(`/admission/leads/${leadId}/waivers`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  reviewLeadWaiver: async (
    waiverId: string,
    data: {
      action: 'approve' | 'approve_modified' | 'reject';
      approved_percentage?: number;
      rejection_reason?: string;
    },
  ) => {
    try {
      const response = await apiClient.put(`/admission/waivers/${waiverId}/review`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Visits
  scheduleVisit: async (leadId: string, data: any) => {
    try {
      const response = await apiClient.post(`/admission/leads/${leadId}/visits`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateVisit: async (visitId: string, data: any) => {
    try {
      const response = await apiClient.put(`/admission/visits/${visitId}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Tests
  scheduleTest: async (leadId: string, data: any) => {
    try {
      const response = await apiClient.post(`/admission/leads/${leadId}/tests`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  submitTestResults: async (testId: string, data: any) => {
    try {
      const response = await apiClient.put(`/admission/tests/${testId}/results`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Interviews
  getLeadInterviews: async (leadId: string) => {
    try {
      const response = await apiClient.get(`/admission/leads/${leadId}/interviews`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  scheduleInterview: async (leadId: string, data: any) => {
    try {
      const response = await apiClient.post(`/admission/leads/${leadId}/interviews`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  recordInterviewOutcome: async (interviewId: string, data: any) => {
    try {
      const response = await apiClient.put(`/admission/interviews/${interviewId}/outcome`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Applications
  createApplication: async (leadId: string, data: any) => {
    try {
      const response = await apiClient.post(`/admission/leads/${leadId}/application`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Direct application (walk-in / no originating lead).
  createApplicationDirect: async (data: any) => {
    try {
      const response = await apiClient.post('/admission/applications', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Carry-forward: fetch a lead's details mapped into the application-form shape.
  getApplicationPrefill: async (leadId: string) => {
    try {
      const response = await apiClient.get(`/admission/leads/${leadId}/application-prefill`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Section auto-assignment rules (configurable) + live per-section preview.
  getSectionRules: async () => {
    try {
      const response = await apiClient.get('/admission/settings/section-rules');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateSectionRules: async (rules: Record<string, unknown>) => {
    try {
      const response = await apiClient.put('/admission/settings/section-rules', rules);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getSectionStats: async (classId: string) => {
    try {
      const response = await apiClient.get('/admission/settings/section-stats', { params: { classId } });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getApplications: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    classId?: string;
    /** e.g. 'enroll_eligibility' — asks the API to embed per-row enroll gating. */
    include?: string;
  }) => {
    try {
      const response = await apiClient.get('/admission/applications', { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Enrollment eligibility (single) — evaluates all nine enrollStudent checks.
  getEnrollEligibility: async (id: string) => {
    try {
      const response = await apiClient.get(`/admission/applications/${id}/enroll-eligibility`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Review-SLA rollup for the approval-queue banner + per-stage breakdown.
  getApplicationsSlaSummary: async () => {
    try {
      const response = await apiClient.get('/admission/applications/sla-summary');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getApplicationById: async (id: string) => {
    try {
      const response = await apiClient.get(`/admission/applications/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Data-management edit of captured application fields (student/guardian/
  // academic details) — NOT a status/decision change. Only allowed while the
  // application is 'submitted' or 'under_review'.
  updateApplication: async (id: string, data: Record<string, unknown>) => {
    try {
      const response = await apiClient.put(`/admission/applications/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getApplicationAllowedActions: async (id: string) => {
    try {
      const response = await apiClient.get(`/admission/applications/${id}/allowed-actions`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  reviewApplication: async (id: string, reviewNotes: string) => {
    try {
      const response = await apiClient.put(`/admission/applications/${id}/review`, { review_notes: reviewNotes });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Paired transition for waitlisted → under_review (Zoho Blueprint
  // transition-button pattern). Dedicated endpoint — NOT reviewApplication —
  // so the server can re-open the waitlist slot, stamp the lead stage back to
  // funnel-active, and audit the paired transition distinctly.
  returnToReview: async (applicationId: string, reason?: string) => {
    try {
      const response = await apiClient.put(
        `/admission/applications/${applicationId}/return-to-review`,
        reason ? { reason } : {},
      );
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  approveApplication: async (id: string, sectionId: string, feeStructureId?: string, decisionNote?: string) => {
    try {
      const response = await apiClient.put(`/admission/applications/${id}/approve`, {
        section_id: sectionId,
        fee_structure_id: feeStructureId,
        decision_note: decisionNote,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  rejectApplication: async (id: string, reason: string) => {
    try {
      const response = await apiClient.put(`/admission/applications/${id}/reject`, { reason });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  waitlistApplication: async (id: string, reason: string, manualPriorityScore?: number) => {
    try {
      const response = await apiClient.put(`/admission/applications/${id}/waitlist`, {
        reason,
        manual_priority_score: manualPriorityScore,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  requestApplicationCorrection: async (
    applicationId: string,
    correctionItems: string[],
    correctionNote: string,
  ) => {
    try {
      const response = await apiClient.put(`/admission/applications/${applicationId}/correction`, {
        correction_items: correctionItems,
        correction_note: correctionNote,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  enrollStudent: async (applicationId: string) => {
    try {
      const response = await apiClient.post(`/admission/applications/${applicationId}/enroll`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  extendApplicationOffer: async (applicationId: string, reason: string, extensionDays?: number) => {
    try {
      const response = await apiClient.post(`/admission/applications/${applicationId}/offer/extend`, {
        reason,
        extension_days: extensionDays,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  expireDueOffers: async () => {
    try {
      const response = await apiClient.post('/admission/offers/expire-due');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // SLA
  getLeadSlaEvents: async (leadId: string) => {
    try {
      const response = await apiClient.get(`/admission/leads/${leadId}/sla`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  snoozeSlaEvent: async (
    slaEventId: string,
    payload: { reason: string; snooze_hours?: number; snoozed_until?: string },
  ) => {
    try {
      const response = await apiClient.put(`/admission/sla-events/${slaEventId}/snooze`, payload);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  resolveSlaEvent: async (slaEventId: string, resolutionNote?: string) => {
    try {
      const response = await apiClient.put(`/admission/sla-events/${slaEventId}/resolve`, {
        resolution_note: resolutionNote,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  evaluateSlaEvents: async (leadId?: string) => {
    try {
      const response = await apiClient.post('/admission/sla-events/evaluate', {
        lead_id: leadId,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Reports
  getFunnelReport: async (params?: { academicYearId?: string; dateFrom?: string; dateTo?: string }) => {
    try {
      const response = await apiClient.get('/admission/reports/funnel', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getSourceAnalysis: async (params?: { academicYearId?: string; dateFrom?: string; dateTo?: string }) => {
    try {
      const response = await apiClient.get('/admission/reports/source-analysis', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Legacy compatibility aliases
  updateStatus: async (id: string, status: string, notes?: string) => {
    try {
      const response = await apiClient.put(`/admission/leads/${id}/stage`, { stage: status, notes });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  convert: async (id: string) => {
    try {
      const response = await apiClient.post(`/admission/applications/${id}/enroll`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// TRANSPORT API
// Backend routes: /transport/*
// ============================================================================
export const transportApi = {
  // ── Routes CRUD ──
  getRoutes: async (params?: any) => {
    try {
      const response = await apiClient.get('/transport', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getRouteById: async (id: string) => {
    try {
      const response = await apiClient.get(`/transport/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createRoute: async (data: any) => {
    try {
      const response = await apiClient.post('/transport', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateRoute: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/transport/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteRoute: async (id: string) => {
    try {
      const response = await apiClient.delete(`/transport/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Stops (nested under route) ──
  getStops: async (routeId: string) => {
    try {
      const response = await apiClient.get(`/transport/${routeId}/stops`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createStop: async (routeId: string, data: any) => {
    try {
      const response = await apiClient.post(`/transport/${routeId}/stops`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateStop: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/transport/stops/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteStop: async (id: string) => {
    try {
      const response = await apiClient.delete(`/transport/stops/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Vehicles ──
  getVehicles: async (params?: any) => {
    try {
      const response = await apiClient.get('/transport/vehicles/list', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createVehicle: async (data: any) => {
    try {
      const response = await apiClient.post('/transport/vehicles', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateVehicle: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/transport/vehicles/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteVehicle: async (id: string) => {
    try {
      const response = await apiClient.delete(`/transport/vehicles/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Student Assignments ──
  getAssignments: async (params?: any) => {
    try {
      const response = await apiClient.get('/transport/assignments/list', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  assignStudent: async (data: any) => {
    try {
      const response = await apiClient.post('/transport/assignments', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateAssignment: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/transport/assignments/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  removeAssignment: async (id: string) => {
    try {
      const response = await apiClient.delete(`/transport/assignments/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Reports ──
  getRouteWiseReport: async (params?: any) => {
    try {
      const response = await apiClient.get('/transport/reports/route-wise', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getUtilizationReport: async (params?: any) => {
    try {
      const response = await apiClient.get('/transport/reports/utilization', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Live Status / Tracking ──
  getTodayRouteStatus: async (routeId: string) => {
    try {
      const response = await apiClient.get(`/transport/status/today/${routeId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  recordPickup: async (data: any) => {
    try {
      const response = await apiClient.post('/transport/tracking/pickup', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  recordDropoff: async (data: any) => {
    try {
      const response = await apiClient.post('/transport/tracking/dropoff', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  markNoShow: async (data: any) => {
    try {
      const response = await apiClient.post('/transport/tracking/no-show', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Transport Fees ──
  recordFeePayment: async (data: any) => {
    try {
      const response = await apiClient.post('/transport/fees/payment', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getFeeStatement: async (studentId: string, params?: { month?: number; year?: number }) => {
    try {
      const response = await apiClient.get(`/transport/fees/statement/${studentId}`, { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  syncTransportToAttendance: async (data: any) => {
    try {
      const response = await apiClient.post('/transport/sync/attendance', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// LIBRARY API
// Backend routes: /library/*
// ============================================================================
export const libraryApi = {
  // ── Books CRUD ──
  getBooks: async (params?: any) => {
    try {
      const response = await apiClient.get('/library/books', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getBookById: async (id: string) => {
    try {
      const response = await apiClient.get(`/library/books/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createBook: async (data: any) => {
    try {
      const response = await apiClient.post('/library/books', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateBook: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/library/books/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteBook: async (id: string) => {
    try {
      const response = await apiClient.delete(`/library/books/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Categories ──
  getCategories: async () => {
    try {
      const response = await apiClient.get('/library/categories');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createCategory: async (data: any) => {
    try {
      const response = await apiClient.post('/library/categories', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateCategory: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/library/categories/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Transactions ──
  getTransactions: async (params?: any) => {
    try {
      const response = await apiClient.get('/library/transactions', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  issueBook: async (data: any) => {
    try {
      const response = await apiClient.post('/library/transactions/issue', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  returnBook: async (id: string) => {
    try {
      const response = await apiClient.post(`/library/transactions/${id}/return`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  renewBook: async (id: string, data?: any) => {
    try {
      const response = await apiClient.post(`/library/transactions/${id}/renew`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  reportLost: async (id: string, payload?: { fineAmount?: number; remarks?: string }) => {
    try {
      const response = await apiClient.post(`/library/transactions/${id}/lost`, payload);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Reports ──
  getOverdueReport: async (params?: any) => {
    try {
      const response = await apiClient.get('/library/reports/overdue', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getCirculationReport: async (params?: any) => {
    try {
      const response = await apiClient.get('/library/reports/circulation', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getPopularBooks: async (params?: any) => {
    try {
      const response = await apiClient.get('/library/reports/popular', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getFinesReport: async (params?: any) => {
    try {
      const response = await apiClient.get('/library/reports/fines', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// INVENTORY API
// Backend routes: /inventory/*
// ============================================================================
export const inventoryApi = {
  // ── Items CRUD ──
  getItems: async (params?: any) => {
    try {
      const response = await apiClient.get('/inventory/items', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getItemById: async (id: string) => {
    try {
      const response = await apiClient.get(`/inventory/items/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createItem: async (data: any) => {
    try {
      const response = await apiClient.post('/inventory/items', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateItem: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/inventory/items/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteItem: async (id: string) => {
    try {
      const response = await apiClient.delete(`/inventory/items/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Vendors CRUD ──
  getVendors: async (params?: any) => {
    try {
      const response = await apiClient.get('/inventory/vendors', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getVendorById: async (id: string) => {
    try {
      const response = await apiClient.get(`/inventory/vendors/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createVendor: async (data: any) => {
    try {
      const response = await apiClient.post('/inventory/vendors', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateVendor: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/inventory/vendors/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteVendor: async (id: string) => {
    try {
      const response = await apiClient.delete(`/inventory/vendors/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Stock Movements ──
  getStockMovements: async (params?: any) => {
    try {
      const response = await apiClient.get('/inventory/stock-movements', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createStockMovement: async (data: any) => {
    try {
      const response = await apiClient.post('/inventory/stock-movements', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Purchase Orders ──
  getPurchaseOrders: async (params?: any) => {
    try {
      const response = await apiClient.get('/inventory/purchase-orders', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getPurchaseOrderById: async (id: string) => {
    try {
      const response = await apiClient.get(`/inventory/purchase-orders/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createPurchaseOrder: async (data: any) => {
    try {
      const response = await apiClient.post('/inventory/purchase-orders', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updatePurchaseOrder: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/inventory/purchase-orders/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  approvePurchaseOrder: async (id: string) => {
    try {
      const response = await apiClient.put(`/inventory/purchase-orders/${id}/approve`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  receivePurchaseOrder: async (id: string, data?: any) => {
    try {
      const response = await apiClient.put(`/inventory/purchase-orders/${id}/receive`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Assets CRUD ──
  getAssets: async (params?: any) => {
    try {
      const response = await apiClient.get('/inventory/assets', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getAssetById: async (id: string) => {
    try {
      const response = await apiClient.get(`/inventory/assets/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createAsset: async (data: any) => {
    try {
      const response = await apiClient.post('/inventory/assets', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateAsset: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/inventory/assets/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteAsset: async (id: string) => {
    try {
      const response = await apiClient.delete(`/inventory/assets/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  allocateAsset: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/inventory/assets/${id}/allocate`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Reports ──
  getStockReport: async (params?: any) => {
    try {
      const response = await apiClient.get('/inventory/reports/stock', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getLowStockReport: async (params?: any) => {
    try {
      const response = await apiClient.get('/inventory/reports/low-stock', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getAssetReport: async (params?: any) => {
    try {
      const response = await apiClient.get('/inventory/reports/assets', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// COMMUNICATION API
// Backend routes: /communication/*
// ============================================================================
export const communicationApi = {
  // ── Announcements ──
  getAnnouncements: async (params?: any) => {
    try {
      const response = await apiClient.get('/communication/announcements', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getAnnouncementById: async (id: string) => {
    try {
      const response = await apiClient.get(`/communication/announcements/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createAnnouncement: async (data: any) => {
    try {
      const response = await apiClient.post('/communication/announcements', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateAnnouncement: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/communication/announcements/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteAnnouncement: async (id: string) => {
    try {
      const response = await apiClient.delete(`/communication/announcements/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  publishAnnouncement: async (id: string) => {
    try {
      const response = await apiClient.put(`/communication/announcements/${id}/publish`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Messages ──
  getMessages: async (params?: any) => {
    try {
      const response = await apiClient.get('/communication/messages', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getMessageById: async (id: string) => {
    try {
      const response = await apiClient.get(`/communication/messages/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  sendMessage: async (data: any) => {
    try {
      const response = await apiClient.post('/communication/messages', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteMessage: async (id: string) => {
    try {
      const response = await apiClient.delete(`/communication/messages/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  markMessageRead: async (id: string) => {
    try {
      const response = await apiClient.put(`/communication/messages/${id}/read`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getMessageThread: async (messageId: string) => {
    try {
      const response = await apiClient.get(`/communication/messages/${messageId}/thread`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Message Templates ──
  getTemplates: async (params?: any) => {
    try {
      const response = await apiClient.get('/communication/templates', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createTemplate: async (data: any) => {
    try {
      const response = await apiClient.post('/communication/templates', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateTemplate: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/communication/templates/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteTemplate: async (id: string) => {
    try {
      const response = await apiClient.delete(`/communication/templates/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Notification Queue ──
  getNotificationQueue: async (params?: any) => {
    try {
      const response = await apiClient.get('/communication/queue', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── SMS Gateway ──
  sendSMS: async (data: any) => {
    try {
      const response = await apiClient.post('/communication/sms/send', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Email ──
  sendEmail: async (data: any) => {
    try {
      const response = await apiClient.post('/communication/email/send', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  sendEmailTemplate: async (data: { email: string; templateId: string; variables?: Record<string, any> }) => {
    try {
      const response = await apiClient.post('/communication/email/template', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// BRANDING API
// School (tenant) identity — distinct from the "EduSphere" product brand.
// Backend routes: /public/branding (no auth) and /settings/branding (auth).
// ============================================================================
export const brandingApi = {
  // Unauthenticated — safe to call before login (login page, browser tab title).
  getPublicBranding: async () => {
    try {
      const response = await apiClient.get('/public/branding');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Authenticated — scoped to the caller's own school.
  getSchoolBranding: async () => {
    try {
      const response = await apiClient.get('/settings/branding');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Authenticated — update the caller's school identity (managers only).
  updateBranding: async (data: any) => {
    try {
      const response = await apiClient.put('/settings/branding', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// SETTINGS API
// Backend routes: /settings/*
// ============================================================================
// ============================================================================
// COMMUNICATION INTEGRATIONS API
// Backend routes: /comms/integrations/*, /comms/automation, /comms/verify/*
// Multi-provider, registry-driven messaging config + phone verification.
// ============================================================================
export const commsSettingsApi = {
  // Static catalogue (channels → providers → fields) for dynamic forms.
  getRegistry: async () => {
    try {
      const response = await apiClient.get('/comms/integrations/registry');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Current per-channel config (secrets masked) + automation switch.
  getConfig: async () => {
    try {
      const response = await apiClient.get('/comms/integrations');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Save a provider's credentials / switch active provider / toggle channel.
  updateChannel: async (
    channel: string,
    data: { provider?: string; fields?: Record<string, unknown>; activeProvider?: string; enabled?: boolean },
  ) => {
    try {
      const response = await apiClient.put(`/comms/integrations/${channel}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Automation master switch (scheduled SMS/WhatsApp).
  setAutomation: async (enabled: boolean) => {
    try {
      const response = await apiClient.put('/comms/automation', { enabled });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Message routing — which channels a purpose uses (otp/fee_reminder/attendance).
  setRouting: async (purpose: string, channels: string[]) => {
    try {
      const response = await apiClient.put(`/comms/integrations/routing/${purpose}`, { channels });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Send a REAL test email to verify the saved SMTP/SendGrid config delivers.
  // Returns { success, to, message }. Optional recipient; defaults to the
  // configured From/username on the server.
  testEmail: async (to?: string) => {
    try {
      const response = await apiClient.post('/comms/integrations/email/test', to ? { to } : {});
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Contact verification (OTP) — the "verified users only" automation gate.
  // The code is delivered over the routed channel (email by default); pass an
  // email for email delivery, or force a channel.
  requestOtp: async (data: {
    phone: string;
    email?: string;
    channel?: 'email' | 'sms' | 'whatsapp';
    subjectType?: string;
    subjectId?: string;
  }) => {
    try {
      const response = await apiClient.post('/comms/verify/request-otp', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  confirmOtp: async (data: { phone: string; otp: string }) => {
    try {
      const response = await apiClient.post('/comms/verify/confirm', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getVerificationStatus: async (phone: string) => {
    try {
      const response = await apiClient.get('/comms/verify/status', { params: { phone } });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  listVerifications: async (params?: { status?: string; limit?: number; offset?: number }) => {
    try {
      const response = await apiClient.get('/comms/verify', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

export const settingsApi = {
  getSchoolSettings: async () => {
    try {
      const response = await apiClient.get('/settings/school');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateSchoolSettings: async (data: any) => {
    try {
      const response = await apiClient.put('/settings/school', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getAuditLogs: async (params?: any) => {
    try {
      const response = await apiClient.get('/settings/audit-logs', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getRoles: async () => {
    try {
      const response = await apiClient.get('/settings/roles');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateRole: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/settings/roles/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Integration Config
  getIntegrations: async () => {
    try {
      const response = await apiClient.get('/settings/integrations');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getIntegrationProvider: async (provider: string) => {
    try {
      const response = await apiClient.get(`/settings/integrations/${provider}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateIntegrationProvider: async (provider: string, data: any) => {
    try {
      const response = await apiClient.put(`/settings/integrations/${provider}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  testIntegration: async (provider: string) => {
    try {
      const response = await apiClient.post(`/settings/integrations/${provider}/test`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Notification Templates (Wave 6)
  getNotificationTemplates: async (params?: { key?: string; channel?: string; language?: string }) => {
    try {
      const response = await apiClient.get('/settings/notification-templates', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateNotificationTemplate: async (
    id: string,
    data: { subject?: string | null; body?: string; is_active?: boolean },
  ) => {
    try {
      const response = await apiClient.put(`/settings/notification-templates/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // First-run onboarding / setup checklist (Wave — Setup Wizard)
  getOnboardingStatus: async () => {
    try {
      const response = await apiClient.get('/settings/onboarding');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// DASHBOARD API
// Backend routes: /dashboard/*
// ============================================================================
export const dashboardApi = {
  getStats: async () => {
    try {
      const response = await apiClient.get('/dashboard/stats');
      const raw = response.data?.data || response.data;
      // Transform backend shape to match frontend dashboardStatsData shape
      const mapped = {
        totalStudents: raw?.students?.total || 0,
        totalStaff: raw?.staff?.total || 0,
        totalTeachers: raw?.staff?.teachers || 0,
        totalClasses: raw?.classes?.total || 0,
        totalSections: raw?.sections?.total || 0,
        attendanceToday: {
          students: {
            present: raw?.attendance?.present || 0,
            absent: raw?.attendance?.absent || 0,
            leave: raw?.attendance?.leave || 0,
            percentage: raw?.attendance?.percentage || 0,
          },
          staff: {
            present: raw?.staff?.present || 0,
            absent: raw?.staff?.absent || 0,
            leave: raw?.staff?.leave || 0,
            percentage: raw?.staff?.total > 0
              ? Math.round((raw.staff.present / raw.staff.total) * 100)
              : 0,
          },
        },
        feeCollection: {
          totalDue: raw?.fees?.totalExpected || 0,
          collected: raw?.fees?.totalCollected || 0,
          pending: raw?.fees?.totalPending || 0,
          collectionRate: raw?.fees?.totalExpected > 0
            ? Math.round((raw.fees.totalCollected / raw.fees.totalExpected) * 100 * 10) / 10
            : 0,
          thisMonth: raw?.fees?.todayCollection || 0,
          defaulters: raw?.fees?.defaulterCount || 0,
        },
        admissions: {
          totalApplications: raw?.admissions?.total || 0,
          approved: raw?.admissions?.approved || 0,
          pending: raw?.admissions?.pending || 0,
          rejected: raw?.admissions?.rejected || 0,
          thisMonth: raw?.students?.newThisMonth || 0,
          conversionRate: raw?.admissions?.conversionRate || 0,
        },
        examResults: raw?.examResults || { lastExam: '', passPercentage: 0, toppers: [] },
        recentActivities: raw?.recentActivities || [],
        upcomingEvents: raw?.upcomingEvents || [],
        monthlyTrends: raw?.monthlyTrends || { collections: [], attendance: [] },
        classWiseStrength: raw?.classWiseStrength || [],
      };
      return { success: true, data: mapped };
    } catch (error: any) {
      return handleError(error);
    }
  },

  getNotifications: async (params?: any) => {
    try {
      const response = await apiClient.get('/dashboard/notifications', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  markNotificationRead: async (id: string) => {
    try {
      const response = await apiClient.put(`/dashboard/notifications/${id}/read`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getAnalytics: async (period: 'monthly' | 'quarterly' = 'monthly') => {
    try {
      const response = await apiClient.get('/dashboard/analytics', { params: { period } });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getTrends: async (metric: 'enrollment' | 'attendance' | 'fees', period: 'monthly' | 'quarterly' = 'monthly') => {
    try {
      const response = await apiClient.get('/dashboard/trends', { params: { metric, period } });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getChartData: async (metric: 'class-wise-strength' | 'fee-collection-breakdown' | 'attendance-status' | 'department-wise' | 'at-risk-students') => {
    try {
      const response = await apiClient.get('/dashboard/chart-data', { params: { metric } });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// ACADEMIC YEAR API
// Backend routes: /academic/academic-years*
// ============================================================================
export const academicYearApi = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/academic/academic-years');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getCurrent: async () => {
    try {
      const response = await apiClient.get('/academic/academic-years/current');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getById: async (id: string) => {
    try {
      const response = await apiClient.get(`/academic/academic-years/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  create: async (data: any) => {
    try {
      const response = await apiClient.post('/academic/academic-years', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  update: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/academic/academic-years/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  delete: async (id: string) => {
    try {
      const response = await apiClient.delete(`/academic/academic-years/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// BRANCH API
// Backend routes: /academic/branches*
// ============================================================================
export const branchApi = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/academic/branches');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getById: async (id: string) => {
    try {
      const response = await apiClient.get(`/academic/branches/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  create: async (data: any) => {
    try {
      const response = await apiClient.post('/academic/branches', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  update: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/academic/branches/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  delete: async (id: string) => {
    try {
      const response = await apiClient.delete(`/academic/branches/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// UPLOAD API
// Backend routes: /upload/*
// ============================================================================
export const uploadApi = {
  uploadDocument: async (formData: FormData) => {
    try {
      const response = await apiClient.post('/upload/document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  uploadPhoto: async (formData: FormData) => {
    try {
      const response = await apiClient.post('/upload/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteDocument: async (id: string) => {
    try {
      const response = await apiClient.delete(`/upload/document/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  verifyDocument: async (id: string) => {
    try {
      const response = await apiClient.put(`/upload/document/${id}/verify`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// CERTIFICATES & REPORTS API
// Backend routes: /reports/*
// ============================================================================
export const certificatesApi = {
  getBonafide: async (studentId: string) => {
    try {
      const response = await apiClient.get(`/reports/students/${studentId}/bonafide`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getTC: async (studentId: string) => {
    try {
      const response = await apiClient.get(`/reports/students/${studentId}/tc`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getIDCard: async (studentId: string) => {
    try {
      const response = await apiClient.get(`/reports/students/${studentId}/id-card`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getBulkIDCards: async (studentIds: string[]) => {
    try {
      const response = await apiClient.post('/reports/students/id-cards', { studentIds });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getFeeReceipt: async (transactionId: string) => {
    try {
      const response = await apiClient.get(`/reports/fees/receipt/${transactionId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// REPORTS API
// Backend routes: /reports/*
// ============================================================================
export const reportsApi = {
  getStudentReport: async (studentId: string) => {
    try {
      const response = await apiClient.get(`/reports/students/${studentId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getClassReport: async (classId: string, sectionId?: string) => {
    try {
      const response = await apiClient.get('/reports/classes', {
        params: { classId, sectionId },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getFinancialReport: async (startDate: string, endDate: string) => {
    try {
      const response = await apiClient.get('/reports/financial', {
        params: { startDate, endDate },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Consolidated Reports (Multi-Branch)
  getConsolidatedStudents: async (filters?: any) => {
    try {
      const response = await apiClient.get('/reports/consolidated/students', {
        params: filters || {},
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getConsolidatedFees: async (filters?: any) => {
    try {
      const response = await apiClient.get('/reports/consolidated/fees', {
        params: filters || {},
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getConsolidatedAttendance: async (filters?: any) => {
    try {
      const response = await apiClient.get('/reports/consolidated/attendance', {
        params: filters || {},
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // PDF Downloads
  getBonafidePDF: async (studentId: string) => {
    try {
      const response = await apiClient.get(
        `/reports/students/${studentId}/bonafide/pdf`,
        { responseType: 'blob' },
      );
      return { success: true, data: response.data as Blob };
    } catch (error: any) {
      return handleError(error);
    }
  },

  getFeeReceiptPDF: async (transactionId: string) => {
    try {
      const response = await apiClient.get(
        `/reports/fees/receipt/${transactionId}/pdf`,
        { responseType: 'blob' },
      );
      return { success: true, data: response.data as Blob };
    } catch (error: any) {
      return handleError(error);
    }
  },

  /** Download a Blob as a file in the browser */
  downloadBlob: (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  // Data Exports (CSV / Excel)
  exportStudents: async (format: 'csv' | 'xlsx' = 'csv', filters?: Record<string, string>) => {
    try {
      const response = await apiClient.get('/reports/students/export', {
        params: { format, ...filters },
        responseType: 'blob',
      });
      return { success: true, data: response.data as Blob };
    } catch (error: any) {
      return handleError(error);
    }
  },

  exportFeeCollection: async (format: 'csv' | 'xlsx' = 'csv', filters?: Record<string, string>) => {
    try {
      const response = await apiClient.get('/reports/fees/collection/export', {
        params: { format, ...filters },
        responseType: 'blob',
      });
      return { success: true, data: response.data as Blob };
    } catch (error: any) {
      return handleError(error);
    }
  },

  exportAttendance: async (format: 'csv' | 'xlsx' = 'csv', filters?: Record<string, string>) => {
    try {
      const response = await apiClient.get('/reports/attendance/export', {
        params: { format, ...filters },
        responseType: 'blob',
      });
      return { success: true, data: response.data as Blob };
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// HOSTEL API
// Backend routes: /hostel/*
// ============================================================================
export const hostelApi = {
  listHostels: async (params?: any) => {
    try {
      const response = await apiClient.get('/hostel/hostels', { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },

  createHostel: async (data: any) => {
    try {
      const response = await apiClient.post('/hostel/hostels', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getHostelById: async (id: string) => {
    try {
      const response = await apiClient.get(`/hostel/hostels/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateHostel: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/hostel/hostels/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteHostel: async (id: string) => {
    try {
      const response = await apiClient.delete(`/hostel/hostels/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  listRooms: async (hostelId: string, params?: any) => {
    try {
      const response = await apiClient.get(`/hostel/hostels/${hostelId}/rooms`, { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },

  listAllocations: async (params?: any) => {
    try {
      const response = await apiClient.get('/hostel/allocations', { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },

  createAllocation: async (data: any) => {
    try {
      const response = await apiClient.post('/hostel/allocations', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  allocateRoom: async (data: any) => {
    try {
      const response = await apiClient.post('/hostel/allocations/allocate-room', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  vacateAllocation: async (id: string) => {
    try {
      const response = await apiClient.put(`/hostel/allocations/${id}/vacate`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  listAvailableRooms: async (hostelId: string, params?: any) => {
    try {
      const response = await apiClient.get(`/hostel/hostels/${hostelId}/available-rooms`, { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },

  createRoom: async (hostelId: string, data: any) => {
    try {
      const response = await apiClient.post(`/hostel/hostels/${hostelId}/rooms`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateRoom: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/hostel/rooms/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteRoom: async (id: string) => {
    try {
      const response = await apiClient.delete(`/hostel/rooms/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  checkRoomAvailability: async (roomId: string) => {
    try {
      const response = await apiClient.get(`/hostel/rooms/${roomId}/availability`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getHostelFees: async (params?: any) => {
    try {
      const response = await apiClient.get('/hostel/fees', { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },

  recordFeePayment: async (data: any) => {
    try {
      const response = await apiClient.post('/hostel/fees/record-payment', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getStudentFeeBalance: async (studentId: string, allocationId: string) => {
    try {
      const response = await apiClient.get(`/hostel/students/${studentId}/allocations/${allocationId}/fee-balance`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getMessMenu: async (hostelId: string) => {
    try {
      const response = await apiClient.get(`/hostel/mess-menu/${hostelId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateMessMenu: async (hostelId: string, data: any) => {
    try {
      const response = await apiClient.put(`/hostel/mess-menu/${hostelId}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getOccupancyReport: async (params?: any) => {
    try {
      const response = await apiClient.get('/hostel/reports/occupancy', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// TIMETABLE API
// Backend routes: /timetable/*
// ============================================================================
export const timetableApi = {
  getSlots: async (params?: any) => {
    try {
      const response = await apiClient.get('/timetable/slots', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createSlot: async (data: any) => {
    try {
      const response = await apiClient.post('/timetable/slots', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Bell Schedule editor (Phase 4) — the backend routes (PUT/DELETE
  // /timetable/slots/:id) existed since Wave 1c but had no frontend caller.
  updateSlot: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/timetable/slots/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteSlot: async (id: string) => {
    try {
      const response = await apiClient.delete(`/timetable/slots/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getEntries: async (params?: any) => {
    try {
      const response = await apiClient.get('/timetable/entries', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createEntry: async (data: any) => {
    try {
      const response = await apiClient.post('/timetable/entries', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getClassTimetable: async (classId: string, sectionId: string) => {
    try {
      const response = await apiClient.get(`/timetable/class/${classId}/section/${sectionId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getTeacherTimetable: async (teacherId: string) => {
    try {
      const response = await apiClient.get(`/timetable/teacher/${teacherId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getConflicts: async () => {
    try {
      const response = await apiClient.get('/timetable/conflicts');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  autoGenerate: async (
    classId: string,
    sectionId: string,
    options?: {
      academicYearId?: string;
      effectiveFrom?: string;
      commit?: boolean;
    }
  ) => {
    try {
      const response = await apiClient.post('/timetable/auto-generate', {
        classId,
        sectionId,
        academicYearId: options?.academicYearId,
        effectiveFrom: options?.effectiveFrom,
        commit: options?.commit ?? false,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  validateEdit: async (data: {
    slotId: string;
    classId: string;
    sectionId: string;
    subjectId: string;
    teacherId: string;
    roomId?: string | null;
    effectiveFrom?: string;
    excludeEntryId?: string;
  }) => {
    try {
      const response = await apiClient.post('/timetable/validate-edit', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  publish: async (classId: string, sectionId: string) => {
    try {
      const response = await apiClient.post('/timetable/publish', {
        classId,
        sectionId,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// ACADEMIC API (workload balancing — Wave 1c)
// Endpoint mounted under /timetable/balance-workload because the academic
// router is locked for this slice. Frontend calls academicApi.balanceWorkload().
// ============================================================================
export const academicApi = {
  balanceWorkload: async (
    classIds: string[],
    sectionIds: string[]
  ): Promise<any> => {
    try {
      const response = await apiClient.post('/timetable/balance-workload', {
        classIds,
        sectionIds,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// ACADEMIC CALENDAR API
// Backend routes: /academic/calendar/*
// ============================================================================
export const academicCalendarApi = {
  listEvents: async (params?: any) => {
    try {
      const response = await apiClient.get('/academic/calendar/events', { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },

  createEvent: async (data: any) => {
    try {
      const response = await apiClient.post('/academic/calendar/events', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateEvent: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/academic/calendar/events/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteEvent: async (id: string) => {
    try {
      const response = await apiClient.delete(`/academic/calendar/events/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// PAYMENT API
// Backend routes: /payments/*
// ============================================================================
export const paymentsApi = {
  initiate: async (studentId: string, amount: number, studentFeeAccountId?: string, description?: string) => {
    try {
      const response = await apiClient.post('/payments/initiate', {
        studentId,
        amount,
        studentFeeAccountId,
        description,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  verify: async (orderId: string, razorpayPaymentId: string, razorpaySignature: string) => {
    try {
      const response = await apiClient.post('/payments/verify', {
        orderId,
        razorpayPaymentId,
        razorpaySignature,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getHistory: async (studentId: string, page: number = 1, pageSize: number = 10) => {
    try {
      const response = await apiClient.get(`/payments/history/${studentId}`, {
        params: { page, pageSize },
      });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },

  getDetails: async (paymentOrderId: string) => {
    try {
      const response = await apiClient.get(`/payments/${paymentOrderId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getStatus: async (paymentOrderId: string) => {
    try {
      const response = await apiClient.get(`/payments/${paymentOrderId}/status`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// SMS API
// Backend routes: /communication/sms/*
// ============================================================================
export const smsApi = {
  send: async (phoneNumber: string, message: string) => {
    try {
      const response = await apiClient.post('/communication/sms/send', {
        phoneNumber,
        message,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  sendBulk: async (classId: string, message: string, sectionId?: string) => {
    try {
      const response = await apiClient.post('/communication/sms/bulk', {
        classId,
        message,
        sectionId,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getBalance: async () => {
    try {
      const response = await apiClient.get('/communication/sms/balance');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// EMAIL API
// Backend routes: /communication/email/*
// ============================================================================
export const emailApi = {
  send: async (email: string, subject: string, htmlBody: string, textBody?: string) => {
    try {
      const response = await apiClient.post('/communication/email/send', {
        email,
        subject,
        htmlBody,
        textBody,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  sendTemplate: async (email: string, templateId: string, variables: Record<string, any>) => {
    try {
      const response = await apiClient.post('/communication/email/template', {
        email,
        templateId,
        variables,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// CONSOLIDATED COMPOSITE ENDPOINTS
// High-impact performance optimizations - reduce N+1 queries
// ============================================================================
export const compositeApi = {
  // GET /students/:id/full-details
  // Returns: student profile + attendance summary + fees summary + exam summary
  // Replaces: 4 separate API calls (getById, getAttendance, getFees, getPerformanceTrend)
  studentFullDetails: async (id: string) => {
    try {
      const response = await apiClient.get(`/students/${id}/full-details`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // POST /attendance/batch
  // Returns: attendance records for multiple students in one class/section
  // Replaces: individual status update calls for each student
  batchAttendance: async (data: {
    date: string;
    classId: string;
    sectionId: string;
    studentIds: string[];
    records: { studentId: string; status: string; remarks?: string }[];
  }) => {
    try {
      const response = await apiClient.post('/attendance/batch', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // GET /academic/classes/with-sections
  // Returns: all classes with their sections, subjects, and student counts
  // Replaces: classesApi.getAll() + multiple classesApi.getSections() calls
  classesWithSections: async () => {
    try {
      const response = await apiClient.get('/academic/classes/with-sections');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // GET /admission/leads/with-details
  // Returns: leads with full contact info, conversion funnel, progress
  // Replaces: leadsApi.getAll() + individual lead detail calls
  leadsWithDetails: async (params?: {
    page?: number;
    limit?: number;
    stage?: string;
    status?: string;
  }) => {
    try {
      const response = await apiClient.get('/admission/leads/with-details', { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },

  // GET /dashboard/principal-overview
  // Returns: aggregated metrics for principal dashboard
  // Replaces: 5+ separate endpoint calls
  principalDashboardOverview: async () => {
    try {
      const response = await apiClient.get('/dashboard/principal-overview');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // GET /dashboard/teacher-overview
  // Returns: class info, attendance, marks, student performance
  // Replaces: multiple teacher dashboard endpoint calls
  teacherDashboardOverview: async (teacherId?: string) => {
    try {
      const response = await apiClient.get('/dashboard/teacher-overview', {
        params: teacherId ? { teacherId } : undefined,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // GET /attendance/class/:classId/section/:sectionId/batch-load
  // Returns: all students + their attendance status for a specific date
  // Replaces: studentsApi.getAll() + attendanceApi.getByDate()
  attendanceWithStudents: async (
    classId: string,
    sectionId: string,
    date: string
  ) => {
    try {
      const response = await apiClient.get(
        `/attendance/class/${classId}/section/${sectionId}/batch-load`,
        { params: { date } }
      );
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// AGGREGATE EXPORT
// Mirrors the `api` default export from api.ts for drop-in replacement
// ============================================================================
// ============================================================================
// WIDGET PLATFORM API
// Backend routes: /widgets/*  (catalog, data, dashboard layouts)
// ============================================================================
export const widgetsApi = {
  getCatalog: async () => {
    try {
      const response = await apiClient.get('/widgets/catalog');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getLayout: async (dashboardKey: string) => {
    try {
      const response = await apiClient.get(`/widgets/dashboards/${dashboardKey}/layout`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  saveLayout: async (dashboardKey: string, layout: unknown[]) => {
    try {
      const response = await apiClient.put(`/widgets/dashboards/${dashboardKey}/layout`, { layout });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  resetLayout: async (dashboardKey: string) => {
    try {
      const response = await apiClient.post(`/widgets/dashboards/${dashboardKey}/layout/reset`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  saveRoleDefault: async (dashboardKey: string, role: string, layout: unknown[]) => {
    try {
      const response = await apiClient.put(`/widgets/dashboards/${dashboardKey}/role-default`, { role, layout });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getWidgetData: async (widgetKey: string) => {
    try {
      const response = await apiClient.get(`/widgets/data/${widgetKey}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getBatchData: async (keys: string[]) => {
    try {
      const response = await apiClient.post('/widgets/data', { keys });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getCustomData: async (config: unknown) => {
    try {
      const response = await apiClient.post('/widgets/custom/data', { config });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

export const api = {
  auth: authApi,
  students: studentsApi,
  employees: employeesApi,
  classes: classesApi,
  sections: sectionsApi,
  subjects: subjectsApi,
  exams: examsApi,
  marks: marksApi,
  attendance: attendanceApi,
  fees: feesApi,
  payments: paymentsApi,
  payroll: payrollApi,
  leads: leadsApi,
  transport: transportApi,
  library: libraryApi,
  inventory: inventoryApi,
  communication: communicationApi,
  sms: smsApi,
  email: emailApi,
  settings: settingsApi,
  dashboard: dashboardApi,
  academicYear: academicYearApi,
  branch: branchApi,
  reports: reportsApi,
  upload: uploadApi,
  certificates: certificatesApi,
  hostel: hostelApi,
  timetable: timetableApi,
  academicCalendar: academicCalendarApi,
  composite: compositeApi,
  widgets: widgetsApi,
};

export default api;

// ============================================================================
// ADMISSIONS & CRM EXTENSIONS API
// Backend routes: /admission/*
// Extends the existing leadsApi with bulk actions, CSV import, rollover,
// conversion, notes, tasks, follow-ups, tags, seats, waitlist, templates,
// and additional reports. Kept as a separate export to avoid mutating the
// existing leadsApi object.
// ============================================================================
export const admissionExtensionsApi = {
  // ── Bulk actions ──
  bulkAssignLeads: async (leadIds: string[], assignedTo: string) => {
    try {
      const response = await apiClient.post('/admission/leads/bulk/assign', {
        lead_ids: leadIds,
        assigned_to: assignedTo,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  bulkUpdateStage: async (leadIds: string[], stage: string, note?: string) => {
    try {
      const response = await apiClient.post('/admission/leads/bulk/stage', {
        lead_ids: leadIds,
        stage,
        note,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  bulkMarkLost: async (leadIds: string[], lostReason: string, notes?: string) => {
    try {
      const response = await apiClient.post('/admission/leads/bulk/mark-lost', {
        lead_ids: leadIds,
        lost_reason: lostReason,
        notes,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  bulkUpdateTags: async (leadIds: string[], tagIds: string[], mode: 'add' | 'remove') => {
    try {
      const response = await apiClient.post('/admission/leads/bulk/tags', {
        lead_ids: leadIds,
        tag_ids: tagIds,
        mode,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  bulkSendTemplate: async (leadIds: string[], templateId: string, channel?: string) => {
    try {
      const response = await apiClient.post('/admission/leads/bulk/send-template', {
        lead_ids: leadIds,
        template_id: templateId,
        channel,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  bulkCreateFollowUps: async (
    leadIds: string[],
    data: {
      followUpType: string;
      dueAt: string;
      assignedTo: string;
      notes?: string;
    },
  ) => {
    try {
      const response = await apiClient.post('/admission/leads/bulk/follow-ups', {
        lead_ids: leadIds,
        follow_up_type: data.followUpType,
        due_at: data.dueAt,
        assigned_to: data.assignedTo,
        notes: data.notes,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  bulkExportLeads: async (filter: Record<string, unknown>): Promise<string> => {
    const response = await apiClient.post('/admission/leads/bulk/export', filter, {
      responseType: 'text',
      transformResponse: [(data) => data],
    });
    return response.data as string;
  },

  // ── CSV import ──
  getImportTemplate: async (): Promise<string> => {
    const response = await apiClient.get('/admission/leads/import/template', {
      responseType: 'text',
      transformResponse: [(data) => data],
    });
    return response.data as string;
  },

  validateImport: async (filename: string, rows: Record<string, unknown>[]) => {
    try {
      const response = await apiClient.post('/admission/leads/import/validate', {
        filename,
        rows,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  commitImport: async (batchId: string) => {
    try {
      const response = await apiClient.post('/admission/leads/import/commit', {
        batch_id: batchId,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Rollover ──
  previewRollover: async (data: {
    fromAcademicYearId: string;
    toAcademicYearId: string;
    stages: string[];
    classIds?: string[];
    ownerIds?: string[];
  }) => {
    try {
      const response = await apiClient.post('/admission/rollover/preview', {
        from_academic_year_id: data.fromAcademicYearId,
        to_academic_year_id: data.toAcademicYearId,
        stages: data.stages,
        class_ids: data.classIds,
        owner_ids: data.ownerIds,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  executeRollover: async (data: {
    runId?: string;
    fromAcademicYearId?: string;
    toAcademicYearId?: string;
    stages?: string[];
    classIds?: string[];
    ownerIds?: string[];
  }) => {
    try {
      // Two-step contract: preview returns runId; execute confirms by run_id.
      // Backward-compat: when no runId is supplied the key is omitted and the
      // server responds 400 with a clear message.
      const body: Record<string, unknown> = { confirm: true };
      if (data.runId) body.run_id = data.runId;
      if (data.fromAcademicYearId) body.from_academic_year_id = data.fromAcademicYearId;
      if (data.toAcademicYearId) body.to_academic_year_id = data.toAcademicYearId;
      if (data.stages) body.stages = data.stages;
      if (data.classIds) body.class_ids = data.classIds;
      if (data.ownerIds) body.owner_ids = data.ownerIds;
      const response = await apiClient.post('/admission/rollover/execute', body);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Conversion ──
  convertLead: async (
    leadId: string,
    data?: {
      applicationId?: string;
      admissionNumber?: string;
      rollNumber?: string;
      sectionId?: string;
    },
  ) => {
    try {
      const response = await apiClient.post(`/admission/leads/${leadId}/convert`, {
        application_id: data?.applicationId,
        admission_number: data?.admissionNumber,
        roll_number: data?.rollNumber,
        section_id: data?.sectionId,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Notes ──
  getLeadNotes: async (leadId: string) => {
    try {
      const response = await apiClient.get(`/admission/leads/${leadId}/notes`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createLeadNote: async (
    leadId: string,
    data: { body: string; isPinned?: boolean; isConfidential?: boolean },
  ) => {
    try {
      const response = await apiClient.post(`/admission/leads/${leadId}/notes`, {
        body: data.body,
        is_pinned: data.isPinned,
        is_confidential: data.isConfidential,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateNote: async (
    noteId: string,
    data: { body?: string; isPinned?: boolean; isConfidential?: boolean },
  ) => {
    try {
      const response = await apiClient.put(`/admission/notes/${noteId}`, {
        body: data.body,
        is_pinned: data.isPinned,
        is_confidential: data.isConfidential,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteNote: async (noteId: string) => {
    try {
      const response = await apiClient.delete(`/admission/notes/${noteId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Tasks ──
  getTasks: async (params?: {
    leadId?: string;
    assignedTo?: string;
    status?: string;
  }) => {
    try {
      const response = await apiClient.get('/admission/tasks', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createLeadTask: async (
    leadId: string,
    data: {
      title: string;
      description?: string;
      priority: string;
      assignedTo?: string;
      dueAt?: string;
    },
  ) => {
    try {
      const response = await apiClient.post(`/admission/leads/${leadId}/tasks`, {
        title: data.title,
        description: data.description,
        priority: data.priority,
        assigned_to: data.assignedTo,
        due_at: data.dueAt,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateTask: async (
    taskId: string,
    data: {
      title?: string;
      description?: string;
      priority?: string;
      assignedTo?: string;
      dueAt?: string;
      status?: string;
    },
  ) => {
    try {
      const response = await apiClient.put(`/admission/tasks/${taskId}`, {
        title: data.title,
        description: data.description,
        priority: data.priority,
        assigned_to: data.assignedTo,
        due_at: data.dueAt,
        status: data.status,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  completeTask: async (taskId: string) => {
    try {
      const response = await apiClient.post(`/admission/tasks/${taskId}/complete`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Follow-ups ──
  getFollowUps: async (params?: {
    leadId?: string;
    assignedTo?: string;
    status?: string;
    dueRange?: string;
  }) => {
    try {
      const response = await apiClient.get('/admission/follow-ups', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createLeadFollowUp: async (
    leadId: string,
    data: {
      followUpType: string;
      dueAt: string;
      assignedTo: string;
      notes?: string;
    },
  ) => {
    try {
      const response = await apiClient.post(`/admission/leads/${leadId}/follow-ups`, {
        follow_up_type: data.followUpType,
        due_at: data.dueAt,
        assigned_to: data.assignedTo,
        notes: data.notes,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  completeFollowUp: async (followUpId: string, completionNote?: string) => {
    try {
      const response = await apiClient.post(`/admission/follow-ups/${followUpId}/complete`, {
        completion_note: completionNote,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  cancelFollowUp: async (followUpId: string) => {
    try {
      const response = await apiClient.post(`/admission/follow-ups/${followUpId}/cancel`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Tags ──
  getTags: async () => {
    try {
      const response = await apiClient.get('/admission/tags');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createTag: async (data: { name: string; color?: string; description?: string }) => {
    try {
      const response = await apiClient.post('/admission/tags', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateTag: async (
    tagId: string,
    data: { name?: string; color?: string; description?: string },
  ) => {
    try {
      const response = await apiClient.put(`/admission/tags/${tagId}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteTag: async (tagId: string) => {
    try {
      const response = await apiClient.delete(`/admission/tags/${tagId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  attachLeadTags: async (leadId: string, tagIds: string[]) => {
    try {
      const response = await apiClient.post(`/admission/leads/${leadId}/tags`, {
        tag_ids: tagIds,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  detachLeadTag: async (leadId: string, tagId: string) => {
    try {
      const response = await apiClient.delete(`/admission/leads/${leadId}/tags/${tagId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Seats ──
  getSeats: async (params?: { academicYearId?: string; classId?: string }) => {
    try {
      const response = await apiClient.get('/admission/seats', {
        params: {
          academic_year_id: params?.academicYearId,
          class_id: params?.classId,
        },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  upsertSeats: async (data: {
    classId: string;
    sectionId?: string;
    totalSeats: number;
    reservedManagement: number;
    reservedRte: number;
    reservedStaff: number;
    reservedSibling: number;
    reservedOpen: number;
  }) => {
    try {
      const response = await apiClient.put('/admission/seats', {
        class_id: data.classId,
        section_id: data.sectionId,
        total_seats: data.totalSeats,
        reserved_management: data.reservedManagement,
        reserved_rte: data.reservedRte,
        reserved_staff: data.reservedStaff,
        reserved_sibling: data.reservedSibling,
        reserved_open: data.reservedOpen,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getSeatAvailability: async (params: {
    classId: string;
    sectionId?: string;
    academicYearId?: string;
  }) => {
    try {
      const response = await apiClient.get('/admission/seats/availability', {
        params: {
          class_id: params.classId,
          section_id: params.sectionId,
          academic_year_id: params.academicYearId,
        },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Waitlist ──
  getWaitlist: async (params?: {
    classId?: string;
    academicYearId?: string;
    status?: string;
  }) => {
    try {
      const response = await apiClient.get('/admission/waitlist', {
        params: {
          class_id: params?.classId,
          academic_year_id: params?.academicYearId,
          status: params?.status,
        },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getWaitlistConfig: async () => {
    try {
      const response = await apiClient.get('/admission/waitlist/config');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateWaitlistConfig: async (data: {
    testScoreWeight: number;
    applicationDateWeight: number;
    interviewScoreWeight: number;
    manualPriorityWeight: number;
  }) => {
    try {
      const response = await apiClient.put('/admission/waitlist/config', {
        test_score_weight: data.testScoreWeight,
        application_date_weight: data.applicationDateWeight,
        interview_score_weight: data.interviewScoreWeight,
        manual_priority_weight: data.manualPriorityWeight,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  recalculateWaitlist: async (classId: string, academicYearId: string) => {
    try {
      const response = await apiClient.post('/admission/waitlist/recalculate', {
        classId,
        academicYearId,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  promoteWaitlistEntry: async (waitlistId: string, offerExpiryDays?: number) => {
    try {
      const response = await apiClient.post(`/admission/waitlist/${waitlistId}/promote`, {
        offer_expiry_days: offerExpiryDays,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Templates ──
  getTemplates: async () => {
    try {
      const response = await apiClient.get('/admission/templates');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getTemplateById: async (templateId: string) => {
    try {
      const response = await apiClient.get(`/admission/templates/${templateId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createTemplate: async (data: {
    code: string;
    name: string;
    triggerEvent: string;
    category: string;
    channels: string[];
    subject?: string;
    body: string;
    isActive: boolean;
  }) => {
    try {
      const response = await apiClient.post('/admission/templates', {
        code: data.code,
        name: data.name,
        trigger_event: data.triggerEvent,
        category: data.category,
        channels: data.channels,
        subject: data.subject,
        body: data.body,
        is_active: data.isActive,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateTemplate: async (
    templateId: string,
    data: {
      code?: string;
      name?: string;
      triggerEvent?: string;
      category?: string;
      channels?: string[];
      subject?: string;
      body?: string;
      isActive?: boolean;
    },
  ) => {
    try {
      const response = await apiClient.put(`/admission/templates/${templateId}`, {
        code: data.code,
        name: data.name,
        trigger_event: data.triggerEvent,
        category: data.category,
        channels: data.channels,
        subject: data.subject,
        body: data.body,
        is_active: data.isActive,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteTemplate: async (templateId: string) => {
    try {
      const response = await apiClient.delete(`/admission/templates/${templateId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  sendLeadTemplate: async (
    leadId: string,
    data: {
      templateId: string;
      channel?: string;
      overrides?: Record<string, unknown>;
    },
  ) => {
    try {
      const response = await apiClient.post(`/admission/leads/${leadId}/send-template`, {
        template_id: data.templateId,
        channel: data.channel,
        overrides: data.overrides,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  sendLeadMessage: async (
    leadId: string,
    data: {
      channel: 'email' | 'sms' | 'whatsapp';
      to?: string;
      cc?: string[];
      bcc?: string[];
      subject?: string;
      body: string;
      isHtml?: boolean;
      attachments?: Array<{ filename: string; type: string; content: string }>;
    },
  ) => {
    try {
      const response = await apiClient.post(`/admission/leads/${leadId}/send-message`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getTemplateActivity: async (params?: {
    from?: string;
    to?: string;
    channel?: string;
    templateCode?: string;
  }) => {
    try {
      const response = await apiClient.get('/admission/templates/activity', {
        params: {
          from: params?.from,
          to: params?.to,
          channel: params?.channel,
          template_code: params?.templateCode,
        },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getAdmissionFunnelReport: async (params?: {
    academicYearId?: string;
    dateFrom?: string;
    dateTo?: string;
    classId?: string;
    source?: string;
    ownerId?: string;
  }) => {
    try {
      const response = await apiClient.get('/admission/reports/funnel', {
        params: {
          academic_year_id: params?.academicYearId,
          date_from: params?.dateFrom,
          date_to: params?.dateTo,
          class_id: params?.classId,
          source: params?.source,
          owner_id: params?.ownerId,
        },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getSourceAnalysisReport: async (params?: {
    academicYearId?: string;
    compareYearId?: string;
    dateFrom?: string;
    dateTo?: string;
    classId?: string;
  }) => {
    try {
      const response = await apiClient.get('/admission/reports/source-analysis', {
        params: {
          academic_year_id: params?.academicYearId,
          compare_year_id: params?.compareYearId,
          date_from: params?.dateFrom,
          date_to: params?.dateTo,
          class_id: params?.classId,
        },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Additional reports ──
  getCounsellorPerformanceReport: async (params?: {
    dateFrom?: string;
    dateTo?: string;
    academicYearId?: string;
  }) => {
    try {
      const response = await apiClient.get('/admission/reports/counsellor-performance', {
        params: {
          date_from: params?.dateFrom,
          date_to: params?.dateTo,
          academic_year_id: params?.academicYearId,
        },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getClassDemandReport: async (params?: { academicYearId?: string; board?: string }) => {
    try {
      const response = await apiClient.get('/admission/reports/class-demand', {
        params: {
          academic_year_id: params?.academicYearId,
          board: params?.board,
        },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getTimeToConvertReport: async (params?: {
    academicYearId?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    try {
      const response = await apiClient.get('/admission/reports/time-to-convert', {
        params: {
          academic_year_id: params?.academicYearId,
          date_from: params?.dateFrom,
          date_to: params?.dateTo,
        },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getDocumentComplianceReport: async (params?: {
    academicYearId?: string;
    classId?: string;
  }) => {
    try {
      const response = await apiClient.get('/admission/reports/document-compliance', {
        params: {
          academic_year_id: params?.academicYearId,
          class_id: params?.classId,
        },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getFinancialReport: async (params?: {
    academicYearId?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    try {
      const response = await apiClient.get('/admission/reports/financial', {
        params: {
          academic_year_id: params?.academicYearId,
          date_from: params?.dateFrom,
          date_to: params?.dateTo,
        },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getLostLeadAnalysisReport: async (params?: {
    academicYearId?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    try {
      const response = await apiClient.get('/admission/reports/lost-lead-analysis', {
        params: {
          academic_year_id: params?.academicYearId,
          date_from: params?.dateFrom,
          date_to: params?.dateTo,
        },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// APPROVAL WORKFLOW ENGINE
// ============================================================================

export const workflowApi = {
  // ── Definitions ────────────────────────────────────────────────────────
  getDefinitions: async () => {
    try {
      const response = await apiClient.get('/workflows/definitions');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getDefinitionByModule: async (module: string) => {
    try {
      const response = await apiClient.get(`/workflows/definitions/module/${module}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getDefinitionById: async (id: string) => {
    try {
      const response = await apiClient.get(`/workflows/definitions/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createDefinition: async (data: {
    module: string;
    name: string;
    description?: string | null;
    stages: Array<{
      stageKey: string;
      stageName: string;
      stageOrder: number;
      allowedRoles: string[];
      requiredApprovals?: number;
      autoApproveConditions?: Array<{ field: string; operator: string; value: string | number | boolean }>;
      escalationHours?: number | null;
      slaHours?: number | null;
    }>;
    transitions?: Array<{ from: string; to: string; conditions?: Record<string, unknown>; requiredRole?: string | null }>;
    isActive?: boolean;
  }) => {
    try {
      const response = await apiClient.post('/workflows/definitions', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateDefinition: async (id: string, data: Record<string, unknown>) => {
    try {
      const response = await apiClient.put(`/workflows/definitions/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteDefinition: async (id: string) => {
    try {
      const response = await apiClient.delete(`/workflows/definitions/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Requests ───────────────────────────────────────────────────────────
  getRequests: async (params?: {
    module?: string;
    status?: string;
    priority?: string;
    requestedBy?: string;
    page?: number;
    pageSize?: number;
  }) => {
    try {
      const response = await apiClient.get('/workflows/requests', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getMyPending: async (params?: { page?: number; pageSize?: number }) => {
    try {
      const response = await apiClient.get('/workflows/requests/my-pending', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getRequestById: async (id: string) => {
    try {
      const response = await apiClient.get(`/workflows/requests/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  submitRequest: async (data: {
    module: string;
    entityType: string;
    entityId: string;
    requestData?: Record<string, unknown> | null;
    priority?: string;
  }) => {
    try {
      const response = await apiClient.post('/workflows/requests', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  performAction: async (requestId: string, data: {
    action: string;
    remarks?: string | null;
  }) => {
    try {
      const response = await apiClient.post(`/workflows/requests/${requestId}/action`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Entity History ─────────────────────────────────────────────────────
  getEntityHistory: async (entityType: string, entityId: string) => {
    try {
      const response = await apiClient.get(`/workflows/entity/${entityType}/${entityId}/history`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getEntityActiveRequest: async (entityType: string, entityId: string) => {
    try {
      const response = await apiClient.get(`/workflows/entity/${entityType}/${entityId}/active`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// TEACHER ASSIGNMENTS
// ============================================================================

export const teacherAssignmentApi = {
  getAll: async (params?: {
    classId?: string;
    sectionId?: string;
    teacherId?: string;
    academicYearId?: string;
    page?: number;
    pageSize?: number;
  }) => {
    try {
      const response = await apiClient.get('/academic/teacher-assignments', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getByTeacher: async (teacherId: string) => {
    try {
      const response = await apiClient.get(`/academic/teacher-assignments/by-teacher/${teacherId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getByClassSection: async (classId: string, sectionId: string) => {
    try {
      const response = await apiClient.get(`/academic/teacher-assignments/by-class/${classId}/section/${sectionId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getWorkloadSummary: async () => {
    try {
      const response = await apiClient.get('/academic/teacher-assignments/workload-summary');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getClassTeachers: async () => {
    try {
      const response = await apiClient.get('/academic/teacher-assignments/class-teachers');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  create: async (data: {
    classId: string;
    sectionId: string;
    subjectId: string;
    teacherId: string;
    periodsPerWeek?: number;
    isClassTeacher?: boolean;
    effectiveFrom?: string;
  }) => {
    try {
      const response = await apiClient.post('/academic/teacher-assignments', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  bulkCreate: async (data: {
    assignments: Array<{
      classId: string;
      sectionId: string;
      subjectId: string;
      teacherId: string;
      periodsPerWeek?: number;
      isClassTeacher?: boolean;
    }>;
    // Required by the backend bulkTeacherAssignmentDto (YYYY-MM-DD).
    effectiveFrom: string;
    allowPartial?: boolean;
  }) => {
    try {
      const response = await apiClient.post('/academic/teacher-assignments/bulk', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  update: async (id: string, data: Record<string, unknown>) => {
    try {
      const response = await apiClient.put(`/academic/teacher-assignments/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  delete: async (id: string) => {
    try {
      const response = await apiClient.delete(`/academic/teacher-assignments/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  replace: async (data: {
    sectionId: string;
    subjectId: string;
    newTeacherId: string;
    expectedVersion?: number;
    reason?: string;
    academicYearId?: string;
  }) => {
    try {
      const response = await apiClient.post('/academic/teacher-assignments/replace', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  setClassTeacher: async (data: {
    classId: string;
    sectionId: string;
    teacherId: string;
  }) => {
    try {
      const response = await apiClient.put('/academic/teacher-assignments/class-teacher', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// TEACHER ELIGIBILITY & TEACHER TYPES (ACADEMIC SETUP REDESIGN)
// ============================================================================

export const teacherEligibilityApi = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/academic/teacher-eligibility');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getTeacherTypes: async () => {
    try {
      const response = await apiClient.get('/academic/teacher-types');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getByTeacher: async (teacherId: string) => {
    try {
      const response = await apiClient.get(`/academic/teacher-eligibility/teacher/${teacherId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getBySubject: async (subjectId: string) => {
    try {
      const response = await apiClient.get(`/academic/teacher-eligibility/subject/${subjectId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getCandidates: async (
    sectionIdOrParams: string | { sectionId: string; subjectId: string; academicYearId?: string },
    subjectId?: string,
    academicYearId?: string,
  ) => {
    try {
      const params =
        typeof sectionIdOrParams === 'object'
          ? sectionIdOrParams
          : { sectionId: sectionIdOrParams, subjectId, academicYearId };
      const response = await apiClient.get('/academic/teacher-eligibility/candidates', {
        params,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  setEligibility: async (
    teacherId: string,
    eligibilities: Array<{ subjectId: string; eligibleClassBands: string[] }>
  ) => {
    try {
      const response = await apiClient.post('/academic/teacher-eligibility', {
        teacherId,
        eligibilities,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  removeEligibility: async (teacherId: string, subjectId: string, academicYearId?: string) => {
    try {
      const response = await apiClient.delete(`/academic/teacher-eligibility/${teacherId}/${subjectId}`, {
        params: { academicYearId },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};


// ============================================================================
// FEE ENTERPRISE CONFIGURATION
// ============================================================================

export const feeEnterpriseApi = {
  // ── Tax Config ─────────────────────────────────────────────────────────
  getTaxConfigs: async () => {
    try {
      const response = await apiClient.get('/fees/tax-config');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createTaxConfig: async (data: Record<string, unknown>) => {
    try {
      const response = await apiClient.post('/fees/tax-config', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateTaxConfig: async (id: string, data: Record<string, unknown>) => {
    try {
      const response = await apiClient.put(`/fees/tax-config/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteTaxConfig: async (id: string) => {
    try {
      const response = await apiClient.delete(`/fees/tax-config/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Receipt Numbering ──────────────────────────────────────────────────
  getReceiptNumbering: async () => {
    try {
      const response = await apiClient.get('/fees/receipt-numbering');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateReceiptNumbering: async (data: Record<string, unknown>) => {
    try {
      const response = await apiClient.put('/fees/receipt-numbering', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Late Fee Rules ─────────────────────────────────────────────────────
  getLateFeeRules: async () => {
    try {
      const response = await apiClient.get('/fees/late-fee-rules');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createLateFeeRule: async (data: Record<string, unknown>) => {
    try {
      const response = await apiClient.post('/fees/late-fee-rules', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateLateFeeRule: async (id: string, data: Record<string, unknown>) => {
    try {
      const response = await apiClient.put(`/fees/late-fee-rules/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteLateFeeRule: async (id: string) => {
    try {
      const response = await apiClient.delete(`/fees/late-fee-rules/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Reminders ──────────────────────────────────────────────────────────
  getReminders: async () => {
    try {
      const response = await apiClient.get('/fees/reminders');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createReminder: async (data: Record<string, unknown>) => {
    try {
      const response = await apiClient.post('/fees/reminders', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateReminder: async (id: string, data: Record<string, unknown>) => {
    try {
      const response = await apiClient.put(`/fees/reminders/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteReminder: async (id: string) => {
    try {
      const response = await apiClient.delete(`/fees/reminders/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Receipt Template Config ────────────────────────────────────────────
  getReceiptTemplateConfig: async () => {
    try {
      const response = await apiClient.get('/fees/receipt-templates/config');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateReceiptTemplateConfig: async (data: Record<string, unknown>) => {
    try {
      const response = await apiClient.put('/fees/receipt-templates/config', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// PAYROLL COMPLIANCE (Indian Statutory)
// ============================================================================

export const payrollComplianceApi = {
  // ── Compliance Config ──────────────────────────────────────────────────
  getComplianceConfig: async () => {
    try {
      const response = await apiClient.get('/payroll/compliance-config');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updatePFConfig: async (data: Record<string, unknown>) => {
    try {
      const response = await apiClient.put('/payroll/compliance-config/pf', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateESIConfig: async (data: Record<string, unknown>) => {
    try {
      const response = await apiClient.put('/payroll/compliance-config/esi', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updatePTConfig: async (data: Record<string, unknown>) => {
    try {
      const response = await apiClient.put('/payroll/compliance-config/pt', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateLWFConfig: async (data: Record<string, unknown>) => {
    try {
      const response = await apiClient.put('/payroll/compliance-config/lwf', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateBonusConfig: async (data: Record<string, unknown>) => {
    try {
      const response = await apiClient.put('/payroll/compliance-config/bonus', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Statutory Bonus Report ───────────────────────────────────────────────
  getBonusReport: async (percent?: number) => {
    try {
      const response = await apiClient.get('/payroll/bonus', {
        params: percent != null ? { percent } : {},
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // W7 — live calculator: calls the real statutoryCompliance compute path
  // (never a client-side re-implementation of PF/ESI/PT math).
  calculateStatutory: async (data: { type: 'pf' | 'esi' | 'pt'; amount: number; period?: 'monthly' | 'half_yearly' }) => {
    try {
      const response = await apiClient.post('/payroll/compliance-config/calculate', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── TDS ────────────────────────────────────────────────────────────────
  getTDSDeclarations: async (params?: { employeeId?: string; financialYear?: string; status?: string }) => {
    try {
      const response = await apiClient.get('/payroll/tds-declarations', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  submitTDSDeclaration: async (data: Record<string, unknown>) => {
    try {
      const response = await apiClient.post('/payroll/tds-declarations', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  approveTDSDeclaration: async (id: string, data?: Record<string, unknown>) => {
    try {
      const response = await apiClient.put(`/payroll/tds-declarations/${id}/approve`, data || {});
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getForm16: async (employeeId: string, financialYear: string) => {
    try {
      const response = await apiClient.get(`/payroll/form-16/${employeeId}/${financialYear}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Salary Advances ────────────────────────────────────────────────────
  getSalaryAdvances: async (params?: { employeeId?: string; status?: string }) => {
    try {
      const response = await apiClient.get('/payroll/salary-advances', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createSalaryAdvance: async (data: Record<string, unknown>) => {
    try {
      const response = await apiClient.post('/payroll/salary-advances', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  approveSalaryAdvance: async (id: string) => {
    try {
      const response = await apiClient.put(`/payroll/salary-advances/${id}/approve`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  rejectSalaryAdvance: async (id: string, reason: string) => {
    try {
      const response = await apiClient.put(`/payroll/salary-advances/${id}/reject`, { reason });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Reimbursements ─────────────────────────────────────────────────────
  getReimbursements: async (params?: { employeeId?: string; status?: string }) => {
    try {
      const response = await apiClient.get('/payroll/reimbursements', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createReimbursement: async (data: Record<string, unknown>) => {
    try {
      const response = await apiClient.post('/payroll/reimbursements', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  approveReimbursement: async (id: string, data: Record<string, unknown>) => {
    try {
      const response = await apiClient.put(`/payroll/reimbursements/${id}/approve`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Payslip Templates ─────────────────────────────────────────────────
  getPayslipTemplates: async () => {
    try {
      const response = await apiClient.get('/payroll/payslip-templates');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createPayslipTemplate: async (data: Record<string, unknown>) => {
    try {
      const response = await apiClient.post('/payroll/payslip-templates', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updatePayslipTemplate: async (id: string, data: Record<string, unknown>) => {
    try {
      const response = await apiClient.put(`/payroll/payslip-templates/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // ── Reports ────────────────────────────────────────────────────────────
  getPFStatement: async (params?: { month?: number; year?: number }) => {
    try {
      const response = await apiClient.get('/payroll/reports/pf-statement', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getESIStatement: async (params?: { month?: number; year?: number }) => {
    try {
      const response = await apiClient.get('/payroll/reports/esi-statement', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getPTStatement: async (params?: { month?: number; year?: number }) => {
    try {
      const response = await apiClient.get('/payroll/reports/pt-statement', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// AUDIT LOG (Enhanced)
// ============================================================================

export const auditApi = {
  getLogs: async (params?: {
    module?: string;
    entityType?: string;
    action?: string;
    performedBy?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    pageSize?: number;
  }) => {
    try {
      const response = await apiClient.get('/audit/logs', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getEntityHistory: async (entityType: string, entityId: string) => {
    try {
      const response = await apiClient.get(`/audit/entity/${entityType}/${entityId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getStats: async () => {
    try {
      const response = await apiClient.get('/audit/stats');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// EXAMINATIONS — ADVANCED (Stream E3)
// Backend routes: /exams/* (split-marks, streams, supplementary, moderation,
// practical scheduling, exemptions).
// ============================================================================
export const examAdvancedApi = {
  // ── Split marks ──
  getSplitConfig: async (examId: string, subjectId: string) => {
    try {
      const r = await apiClient.get(`/exams/${examId}/subjects/${subjectId}/split-config`);
      return r.data;
    } catch (e: any) { return handleError(e); }
  },
  configureSplit: async (examId: string, subjectId: string, splits: any[]) => {
    try {
      const r = await apiClient.post(
        `/exams/${examId}/subjects/${subjectId}/split-config`,
        { splits },
      );
      return r.data;
    } catch (e: any) { return handleError(e); }
  },
  saveSplitMarks: async (
    examId: string,
    subjectId: string,
    body: {
      student_id: string;
      class_id: string;
      section_id: string;
      components: Array<{
        component_kind: string;
        marks_obtained: number | null;
        is_absent?: boolean;
        remarks?: string | null;
      }>;
    },
  ) => {
    try {
      const r = await apiClient.post(
        `/exams/${examId}/subjects/${subjectId}/split-marks`,
        body,
      );
      return r.data;
    } catch (e: any) { return handleError(e); }
  },
  getStudentSplitMarks: async (examId: string, subjectId: string, studentId: string) => {
    try {
      const r = await apiClient.get(
        `/exams/${examId}/subjects/${subjectId}/split-marks/${studentId}`,
      );
      return r.data;
    } catch (e: any) { return handleError(e); }
  },

  // ── Streams ──
  listStreams: async () => {
    try { return (await apiClient.get('/exams/streams')).data; }
    catch (e: any) { return handleError(e); }
  },
  getStream: async (id: string) => {
    try { return (await apiClient.get(`/exams/streams/${id}`)).data; }
    catch (e: any) { return handleError(e); }
  },
  createStream: async (data: any) => {
    try { return (await apiClient.post('/exams/streams', data)).data; }
    catch (e: any) { return handleError(e); }
  },
  updateStream: async (id: string, data: any) => {
    try { return (await apiClient.put(`/exams/streams/${id}`, data)).data; }
    catch (e: any) { return handleError(e); }
  },
  deleteStream: async (id: string) => {
    try { return (await apiClient.delete(`/exams/streams/${id}`)).data; }
    catch (e: any) { return handleError(e); }
  },
  assignStreamStudents: async (streamId: string, studentIds: string[]) => {
    try {
      return (await apiClient.post(`/exams/streams/${streamId}/assign`, { student_ids: studentIds })).data;
    } catch (e: any) { return handleError(e); }
  },
  getStudentStreamSubjects: async (studentId: string) => {
    try {
      return (await apiClient.get(`/exams/students/${studentId}/stream-subjects`)).data;
    } catch (e: any) { return handleError(e); }
  },

  // ── Supplementary exams ──
  listSupplementary: async (parentExamId?: string) => {
    try {
      const r = await apiClient.get('/exams/supplementary', {
        params: parentExamId ? { parentExamId } : undefined,
      });
      return r.data;
    } catch (e: any) { return handleError(e); }
  },
  createSupplementary: async (data: any) => {
    try { return (await apiClient.post('/exams/supplementary', data)).data; }
    catch (e: any) { return handleError(e); }
  },
  getSupplementary: async (id: string) => {
    try { return (await apiClient.get(`/exams/supplementary/${id}`)).data; }
    catch (e: any) { return handleError(e); }
  },
  autoElectSupplementary: async (id: string) => {
    try { return (await apiClient.post(`/exams/supplementary/${id}/auto-elect`)).data; }
    catch (e: any) { return handleError(e); }
  },
  registerSupplementaryCandidate: async (id: string, data: any) => {
    try { return (await apiClient.post(`/exams/supplementary/${id}/register`, data)).data; }
    catch (e: any) { return handleError(e); }
  },
  markSupplementaryCompleted: async (id: string, data: any) => {
    try { return (await apiClient.post(`/exams/supplementary/${id}/mark-completed`, data)).data; }
    catch (e: any) { return handleError(e); }
  },
  scheduleSupplementary: async (id: string, data: any) => {
    try { return (await apiClient.post(`/exams/supplementary/${id}/schedule`, data)).data; }
    catch (e: any) { return handleError(e); }
  },
  unregisterCandidate: async (candidateId: string) => {
    try { return (await apiClient.delete(`/exams/supplementary/candidates/${candidateId}`)).data; }
    catch (e: any) { return handleError(e); }
  },

  // ── Bell-curve moderation ──
  analyzeSubject: async (examId: string, params: { subjectId: string; classId?: string; sectionId?: string }) => {
    try {
      return (await apiClient.get(`/exams/${examId}/moderation/analyze`, { params })).data;
    } catch (e: any) { return handleError(e); }
  },
  proposeModeration: async (examId: string, data: any) => {
    try {
      return (await apiClient.post(`/exams/${examId}/moderation/propose`, data)).data;
    } catch (e: any) { return handleError(e); }
  },
  listProposals: async (params?: { examId?: string; status?: string }) => {
    try {
      return (await apiClient.get('/exams/moderation/proposals', { params })).data;
    } catch (e: any) { return handleError(e); }
  },
  approveProposal: async (id: string) => {
    try { return (await apiClient.post(`/exams/moderation/proposals/${id}/approve`)).data; }
    catch (e: any) { return handleError(e); }
  },
  rejectProposal: async (id: string, notes?: string) => {
    try { return (await apiClient.post(`/exams/moderation/proposals/${id}/reject`, { notes })).data; }
    catch (e: any) { return handleError(e); }
  },
  applyProposal: async (id: string) => {
    try { return (await apiClient.post(`/exams/moderation/proposals/${id}/apply`)).data; }
    catch (e: any) { return handleError(e); }
  },

  // ── Practical scheduling ──
  createPracticalSlots: async (examId: string, data: any) => {
    try { return (await apiClient.post(`/exams/${examId}/practical/slots`, data)).data; }
    catch (e: any) { return handleError(e); }
  },
  listPracticalSlots: async (examId: string, subjectId?: string) => {
    try {
      return (await apiClient.get(`/exams/${examId}/practical/slots`, {
        params: subjectId ? { subjectId } : undefined,
      })).data;
    } catch (e: any) { return handleError(e); }
  },
  updatePracticalSlot: async (slotId: string, data: any) => {
    try { return (await apiClient.put(`/exams/practical/slots/${slotId}`, data)).data; }
    catch (e: any) { return handleError(e); }
  },
  deletePracticalSlot: async (slotId: string) => {
    try { return (await apiClient.delete(`/exams/practical/slots/${slotId}`)).data; }
    catch (e: any) { return handleError(e); }
  },
  autoAllocatePractical: async (examId: string, data: any) => {
    try { return (await apiClient.post(`/exams/${examId}/practical/auto-allocate`, data)).data; }
    catch (e: any) { return handleError(e); }
  },
  listAllocations: async (slotId: string) => {
    try { return (await apiClient.get(`/exams/practical/slots/${slotId}/allocations`)).data; }
    catch (e: any) { return handleError(e); }
  },
  markPracticalAttendance: async (slotId: string, data: any) => {
    try { return (await apiClient.post(`/exams/practical/slots/${slotId}/attendance`, data)).data; }
    catch (e: any) { return handleError(e); }
  },

  // ── Exemptions ──
  listExemptions: async (params?: { examId?: string; status?: string; studentId?: string }) => {
    try { return (await apiClient.get('/exams/exemptions', { params })).data; }
    catch (e: any) { return handleError(e); }
  },
  requestExemption: async (data: any) => {
    try { return (await apiClient.post('/exams/exemptions', data)).data; }
    catch (e: any) { return handleError(e); }
  },
  approveExemption: async (id: string, notes?: string) => {
    try { return (await apiClient.post(`/exams/exemptions/${id}/approve`, { notes })).data; }
    catch (e: any) { return handleError(e); }
  },
  rejectExemption: async (id: string, notes?: string) => {
    try { return (await apiClient.post(`/exams/exemptions/${id}/reject`, { notes })).data; }
    catch (e: any) { return handleError(e); }
  },
  listExamExemptions: async (examId: string) => {
    try { return (await apiClient.get(`/exams/exemptions/exam/${examId}`)).data; }
    catch (e: any) { return handleError(e); }
  },
};

// ---------------------------------------------------------------------------
// Module-specific typed API surfaces (Phase 0+ rebuild)
// ---------------------------------------------------------------------------
// Each module owns its own api file under src/data/<module>.api.ts.
// Consumers should import them directly to avoid circular re-export issues:
//   import { feeComponentsApi } from '@/data/finance.api';
//   import { siblingGroupApi } from '@/data/students.api';
//   import { gratuityApi } from '@/data/hr.api';
// etc.

// ============================================================================
// E5 — HR / Payroll advanced business logic API surfaces
// ============================================================================

export const daRevisionApi = {
  list: async () => {
    try { return (await apiClient.get('/payroll/da-revisions')).data; }
    catch (e: any) { return handleError(e); }
  },
  propose: async (data: {
    applicableFrom: string;
    daRatePct: number;
    aicpiBasis?: number | null;
    governmentOrderRef?: string | null;
    applicableCategories?: string[] | null;
  }) => {
    try { return (await apiClient.post('/payroll/da-revisions', data)).data; }
    catch (e: any) { return handleError(e); }
  },
  approve: async (id: string) => {
    try { return (await apiClient.put(`/payroll/da-revisions/${id}/approve`)).data; }
    catch (e: any) { return handleError(e); }
  },
  apply: async (id: string) => {
    try { return (await apiClient.put(`/payroll/da-revisions/${id}/apply`)).data; }
    catch (e: any) { return handleError(e); }
  },
};

export const annualIncrementApi = {
  listPolicies: async () => {
    try { return (await apiClient.get('/payroll/increment-policies')).data; }
    catch (e: any) { return handleError(e); }
  },
  createPolicy: async (data: {
    name: string;
    policyKind: 'fixed_amount' | 'percentage' | 'grade_based';
    amountOrPct: number;
    effectiveDateKind: 'jan_1' | 'jul_1' | 'anniversary';
    appliesToDesignationIds?: string[] | null;
  }) => {
    try { return (await apiClient.post('/payroll/increment-policies', data)).data; }
    catch (e: any) { return handleError(e); }
  },
  listRuns: async () => {
    try { return (await apiClient.get('/payroll/increment-runs')).data; }
    catch (e: any) { return handleError(e); }
  },
  previewRun: async (data: { policyId: string; effectiveDate: string }) => {
    try { return (await apiClient.post('/payroll/increment-runs/preview', data)).data; }
    catch (e: any) { return handleError(e); }
  },
  getRunDetails: async (id: string) => {
    try { return (await apiClient.get(`/payroll/increment-runs/${id}/details`)).data; }
    catch (e: any) { return handleError(e); }
  },
  executeRun: async (id: string) => {
    try { return (await apiClient.put(`/payroll/increment-runs/${id}/execute`)).data; }
    catch (e: any) { return handleError(e); }
  },
};

export const salaryArrearsApi = {
  list: async (params?: { reason?: string; onlyPending?: boolean }) => {
    try { return (await apiClient.get('/payroll/arrears', { params })).data; }
    catch (e: any) { return handleError(e); }
  },
  compute: async (data: {
    employeeId: string;
    fromDate: string;
    toDate: string;
    reason: 'da_backdated' | 'increment_backdated' | 'admin_correction' | 'pay_revision' | 'other';
    oldMonthlyTotal?: number;
    newMonthlyTotal?: number;
    monthlyDelta?: number;
    notes?: string;
  }) => {
    try { return (await apiClient.post('/payroll/arrears/compute', data)).data; }
    catch (e: any) { return handleError(e); }
  },
  applyToRun: async (id: string, payrollRunId: string) => {
    try {
      return (await apiClient.put(`/payroll/arrears/${id}/apply-to-run`, { payrollRunId })).data;
    } catch (e: any) { return handleError(e); }
  },
};

export const leaveEncashmentApi = {
  list: async (status?: string) => {
    try { return (await apiClient.get('/payroll/leave-encashments', { params: { status } })).data; }
    catch (e: any) { return handleError(e); }
  },
  compute: async (data: {
    employeeId: string;
    encashDays: number;
    occasionDate: string;
    reason: 'resignation' | 'retirement' | 'annual_encashment' | 'transfer' | 'death';
  }) => {
    try { return (await apiClient.post('/payroll/leave-encashments/compute', data)).data; }
    catch (e: any) { return handleError(e); }
  },
  request: async (data: {
    employeeId: string;
    encashDays: number;
    occasionDate: string;
    reason: 'resignation' | 'retirement' | 'annual_encashment' | 'transfer' | 'death';
  }) => {
    try { return (await apiClient.post('/payroll/leave-encashments', data)).data; }
    catch (e: any) { return handleError(e); }
  },
  approve: async (id: string) => {
    try { return (await apiClient.put(`/payroll/leave-encashments/${id}/approve`)).data; }
    catch (e: any) { return handleError(e); }
  },
  reject: async (id: string) => {
    try { return (await apiClient.put(`/payroll/leave-encashments/${id}/reject`)).data; }
    catch (e: any) { return handleError(e); }
  },
  pay: async (id: string, payrollRunId: string) => {
    try {
      return (await apiClient.put(`/payroll/leave-encashments/${id}/pay`, { payrollRunId })).data;
    } catch (e: any) { return handleError(e); }
  },
};

export const employeeLoanApi = {
  list: async (status?: string) => {
    try { return (await apiClient.get('/payroll/loans', { params: { status } })).data; }
    catch (e: any) { return handleError(e); }
  },
  request: async (data: {
    employeeId: string;
    principalAmount: number;
    emiCount: number;
    interestRatePct?: number;
    startPayrollPeriod: string;
    purpose?: string;
  }) => {
    try { return (await apiClient.post('/payroll/loans', data)).data; }
    catch (e: any) { return handleError(e); }
  },
  approve: async (id: string) => {
    try { return (await apiClient.put(`/payroll/loans/${id}/approve`)).data; }
    catch (e: any) { return handleError(e); }
  },
  reject: async (id: string) => {
    try { return (await apiClient.put(`/payroll/loans/${id}/reject`)).data; }
    catch (e: any) { return handleError(e); }
  },
  disburse: async (id: string, payrollRunId: string) => {
    try { return (await apiClient.post(`/payroll/loans/${id}/disburse`, { payrollRunId })).data; }
    catch (e: any) { return handleError(e); }
  },
  getDeductions: async (id: string) => {
    try { return (await apiClient.get(`/payroll/loans/${id}/deductions`)).data; }
    catch (e: any) { return handleError(e); }
  },
};

export const bankFileExportApi = {
  list: async () => {
    try { return (await apiClient.get('/payroll/bank-files')).data; }
    catch (e: any) { return handleError(e); }
  },
  generate: async (data: {
    payrollRunId: string;
    fileFormat: 'nach' | 'icici_mab' | 'hdfc_corporate' | 'sbi_corporate' | 'custom_csv';
    schoolBankAccountId?: string;
  }) => {
    try { return (await apiClient.post('/payroll/bank-files/generate', data)).data; }
    catch (e: any) { return handleError(e); }
  },
  updateStatus: async (id: string, data: {
    status: 'generated' | 'submitted' | 'acknowledged' | 'rejected' | 'reconciled';
    notes?: string;
    bankResponseUrl?: string;
  }) => {
    try { return (await apiClient.put(`/payroll/bank-files/${id}/status`, data)).data; }
    catch (e: any) { return handleError(e); }
  },
  downloadUrl: (id: string) => `/api/v1/payroll/bank-files/${id}/download`,
};

export const form24QApi = {
  list: async () => {
    try { return (await apiClient.get('/payroll/form24q')).data; }
    catch (e: any) { return handleError(e); }
  },
  generate: async (data: { financialYear: string; quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4' }) => {
    try { return (await apiClient.post('/payroll/form24q/generate', data)).data; }
    catch (e: any) { return handleError(e); }
  },
  markFiled: async (id: string, acknowledgementNo: string) => {
    try {
      return (await apiClient.put(`/payroll/form24q/${id}/mark-filed`, { acknowledgementNo })).data;
    } catch (e: any) { return handleError(e); }
  },
  markAccepted: async (id: string) => {
    try { return (await apiClient.put(`/payroll/form24q/${id}/mark-accepted`)).data; }
    catch (e: any) { return handleError(e); }
  },
  markRejected: async (id: string) => {
    try { return (await apiClient.put(`/payroll/form24q/${id}/mark-rejected`)).data; }
    catch (e: any) { return handleError(e); }
  },
  downloadUrl: (id: string) => `/api/v1/payroll/form24q/${id}/download`,
};

// ============================================================================
// ADMISSION INTELLIGENCE API (appended — Admissions/CRM advanced features)
// Per-lead communication history + reusable report/SLA reads for the
// dashboard intelligence widgets. APPEND-ONLY; do not restructure above.
// ============================================================================

export interface AdmissionCommunicationLog {
  id: string;
  lead_id: string;
  template_id: string | null;
  channel: 'whatsapp' | 'sms' | 'email' | 'internal' | string;
  recipient: string;
  rendered_subject: string | null;
  rendered_body: string;
  status: 'queued' | 'sent' | 'delivered' | 'failed' | 'dnd_blocked' | string;
  error_message: string | null;
  trigger_type: 'system' | 'manual' | 'bulk' | string;
  triggered_by: string | null;
  sent_at: string | null;
  created_at: string;
  template_name: string | null;
  template_code: string | null;
}

export const admissionIntelligenceApi = {
  /**
   * Unified send log for a single lead.
   * Backend (additive): GET /admission/leads/:leadId/communications
   * -> { success, data: AdmissionCommunicationLog[] } (newest first)
   */
  getLeadCommunications: async (leadId: string) => {
    try {
      const response = await apiClient.get(
        `/admission/leads/${leadId}/communications`,
      );
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  /** SLA events for a single lead. GET /admission/leads/:leadId/sla */
  getLeadSla: async (leadId: string) => {
    try {
      const response = await apiClient.get(`/admission/leads/${leadId}/sla`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  /** Counsellor performance report (reused for dashboard). */
  getCounsellorPerformance: async (params?: Record<string, unknown>) => {
    try {
      const response = await apiClient.get(
        '/admission/reports/counsellor-performance',
        { params },
      );
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// EXPENSES / DAY-BOOK API
// Backend routes: /expenses/*
// CRUD scoped by branch, a pending -> approved/rejected approval workflow,
// a day-book (cash-in vs cash-out with running balance) and a per-category
// spend summary.
// ============================================================================
export const expensesApi = {
  list: async (params?: {
    page?: number;
    pageSize?: number;
    category?: string;
    status?: string;
    paymentMode?: string;
    search?: string;
    fromDate?: string;
    toDate?: string;
    sortField?: string;
    sortDirection?: 'asc' | 'desc';
  }) => {
    try {
      const response = await apiClient.get('/expenses', { params });
      return transformPaginated(response);
    } catch (error: any) {
      return handleError(error);
    }
  },

  getById: async (id: string) => {
    try {
      const response = await apiClient.get(`/expenses/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  create: async (data: {
    category: string;
    description: string;
    amount: number;
    expenseDate?: string;
    paymentMode: string;
    vendor?: string;
    tdsSection?: string;
  }) => {
    try {
      const response = await apiClient.post('/expenses', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  update: async (
    id: string,
    data: Partial<{
      category: string;
      description: string;
      amount: number;
      expenseDate: string;
      paymentMode: string;
      vendor: string | null;
      tdsSection: string | null;
    }>,
  ) => {
    try {
      const response = await apiClient.put(`/expenses/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  remove: async (id: string) => {
    try {
      const response = await apiClient.delete(`/expenses/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  approve: async (id: string) => {
    try {
      const response = await apiClient.put(`/expenses/${id}/approve`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  reject: async (id: string, reason?: string) => {
    try {
      const response = await apiClient.put(`/expenses/${id}/reject`, { reason });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  dayBook: async (params: { fromDate: string; toDate: string; openingBalance?: number }) => {
    try {
      const response = await apiClient.get('/expenses/day-book', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  categorySummary: async (params?: { fromDate?: string; toDate?: string }) => {
    try {
      const response = await apiClient.get('/expenses/summary/category', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getVendorTdsSummary: async (fromDate?: string, toDate?: string) => {
    try {
      const response = await apiClient.get('/expenses/summary/vendor-tds', {
        params: { fromDate, toDate },
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getTdsSections: async () => {
    try {
      const response = await apiClient.get('/expenses/tds-sections');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// RTE (Right to Education, 25% quota) COMPLIANCE — Phase 2
// ============================================================================

export const rteApi = {
  ceiling: async (params: { sectionId: string; academicYearId: string }) => {
    try {
      const response = await apiClient.get('/rte/ceiling', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  intakeReport: async (params: { academicYearId: string; branchId?: string; classId?: string; sectionId?: string }) => {
    try {
      const response = await apiClient.get('/rte/intake-report', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  list: async (params?: { branchId?: string; academicYearId?: string; status?: string }) => {
    try {
      const response = await apiClient.get('/rte/annexure-iv', { params });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getByStudent: async (studentId: string, academicYearId?: string) => {
    try {
      const response = await apiClient.get(`/rte/annexure-iv/student/${studentId}`, {
        params: academicYearId ? { academicYearId } : undefined,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getById: async (id: string) => {
    try {
      const response = await apiClient.get(`/rte/annexure-iv/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  create: async (data: Record<string, unknown>) => {
    try {
      const response = await apiClient.post('/rte/annexure-iv', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  update: async (id: string, data: Record<string, unknown>) => {
    try {
      const response = await apiClient.patch(`/rte/annexure-iv/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  submit: async (id: string) => {
    try {
      const response = await apiClient.post(`/rte/annexure-iv/${id}/submit`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  approve: async (id: string, remarks?: string) => {
    try {
      const response = await apiClient.post(`/rte/annexure-iv/${id}/approve`, { remarks });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  reject: async (id: string, reason: string) => {
    try {
      const response = await apiClient.post(`/rte/annexure-iv/${id}/reject`, { reason });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  remove: async (id: string) => {
    try {
      const response = await apiClient.delete(`/rte/annexure-iv/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

// ============================================================================
// LEAD CADENCES API  (Lead Engagement / Follow-up Cadence Builder, migration 150)
// Backend routes: /admission/cadences/*, /admission/enrollments/*, /admission/step-runs/*
// ============================================================================

export const cadencesApi = {
  // List working (non-template) cadences. Pass includeTemplates to also get presets.
  list: async (includeTemplates = false) => {
    try {
      const response = await apiClient.get('/admission/cadences', {
        params: includeTemplates ? { includeTemplates: 'true' } : undefined,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Industry preset cadences (is_template=true), each with its steps embedded.
  templates: async () => {
    try {
      const response = await apiClient.get('/admission/cadences/templates');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  get: async (id: string) => {
    try {
      const response = await apiClient.get(`/admission/cadences/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  create: async (data: {
    name: string;
    description?: string;
    trigger_event?: string;
    is_active?: boolean;
    steps?: any[];
  }) => {
    try {
      const response = await apiClient.post('/admission/cadences', data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  update: async (id: string, data: any) => {
    try {
      const response = await apiClient.put(`/admission/cadences/${id}`, data);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  saveSteps: async (id: string, steps: any[]) => {
    try {
      const response = await apiClient.put(`/admission/cadences/${id}/steps`, { steps });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  remove: async (id: string) => {
    try {
      const response = await apiClient.delete(`/admission/cadences/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  duplicate: async (id: string) => {
    try {
      const response = await apiClient.post(`/admission/cadences/${id}/duplicate-as-template`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  enroll: async (id: string, leadIds: string[]) => {
    try {
      const response = await apiClient.post(`/admission/cadences/${id}/enroll`, {
        lead_ids: leadIds,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  enrollments: async (id: string) => {
    try {
      const response = await apiClient.get(`/admission/cadences/${id}/enrollments`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  cancelEnrollment: async (enrollmentId: string) => {
    try {
      const response = await apiClient.post(`/admission/enrollments/${enrollmentId}/cancel`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  recordOutcome: async (stepRunId: string, outcome: string) => {
    try {
      const response = await apiClient.post(`/admission/step-runs/${stepRunId}/outcome`, {
        outcome,
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  // Admin manual trigger of the processor (verification / catch-up).
  processNow: async () => {
    try {
      const response = await apiClient.post('/admission/cadences/process');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

export const customFieldsApi = {
  listDefinitions: async (entity?: string) => {
    try {
      const response = await apiClient.get('/custom-fields/definitions', {
        params: entity ? { entity } : {},
      });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createDefinition: async (payload: any) => {
    try {
      const response = await apiClient.post('/custom-fields/definitions', payload);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateDefinition: async (id: string, payload: any) => {
    try {
      const response = await apiClient.put(`/custom-fields/definitions/${id}`, payload);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteDefinition: async (id: string) => {
    try {
      const response = await apiClient.delete(`/custom-fields/definitions/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  getValues: async (entity: string, entityId: string) => {
    try {
      const response = await apiClient.get(`/custom-fields/values/${entity}/${entityId}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  setValues: async (entity: string, entityId: string, values: Record<string, any>) => {
    try {
      const response = await apiClient.put(`/custom-fields/values/${entity}/${entityId}`, { values });
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

export const customReportsApi = {
  getEntities: async () => {
    try {
      const response = await apiClient.get('/custom-reports/entities');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  run: async (spec: {
    baseEntity: string;
    columns: string[];
    filters: { column: string; op: string; value: string | string[] }[];
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    limit?: number;
  }) => {
    try {
      const response = await apiClient.post('/custom-reports/run', spec);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  listDefinitions: async () => {
    try {
      const response = await apiClient.get('/custom-reports/definitions');
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  createDefinition: async (payload: any) => {
    try {
      const response = await apiClient.post('/custom-reports/definitions', payload);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  updateDefinition: async (id: string, payload: any) => {
    try {
      const response = await apiClient.put(`/custom-reports/definitions/${id}`, payload);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  deleteDefinition: async (id: string) => {
    try {
      const response = await apiClient.delete(`/custom-reports/definitions/${id}`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },

  runSaved: async (id: string) => {
    try {
      const response = await apiClient.get(`/custom-reports/definitions/${id}/run`);
      return response.data;
    } catch (error: any) {
      return handleError(error);
    }
  },
};

